/** The AutoCAD interface, in parts, to be composed round our own canvas.
 *
 * ⚠⚠ **THIS IS A FIDELITY EXERCISE, NOT A UI DESIGN.** The brief is the one the SketchUp lane
 * was given: the same buttons, in the same arrangement, doing the same things, with the
 * dropdowns behaving the way AutoCAD's do — and anything not built yet GREYED rather than
 * hidden. Every deviation costs the mode its whole point, which is that a drafter who already
 * knows AutoCAD does not have to be taught anything. So the ribbon is a ribbon and not a tab
 * strip, the panel titles sit UNDER the panels, the split buttons drop where AutoCAD's drop,
 * the command line is a real command line, and the coordinates read out bottom-left.
 *
 * ⚠⚠ **GREY MEANS "NOT BUILT", AND IT IS COMPUTED, NOT DECLARED.** An item is live exactly when
 * `handlers.ts` gives it a `run` (or, for a dropdown, a `select`). There is no `implemented`
 * column anywhere. Hover a grey item and the tooltip says why — "not built yet", or where the
 * feature actually lives. The failure this avoids is a button that looks alive and quietly does
 * nothing, which is how somebody loses ten minutes deciding whether they clicked wrong.
 *
 * ⚠⚠ **THE COMMAND LINE IS A VIEW OF THE SAME TABLE, NOT A SECOND IMPLEMENTATION.** Typing
 * `TRIM` resolves through `catalog.ts` to the id the ribbon button carries, and runs the same
 * handler. So a command cannot be typable and greyed at once, and wiring one place wires both.
 * A command line with its own dispatch would be a second product inside the first, and the two
 * would disagree within a week.
 *
 * ⚠⚠ **PARTS, NOT A WRAPPER.** `DesignStudioPage` already has this shape — header rows, a
 * `flex: 1` band holding rail / canvas / palettes, a status bar — and it is a file several
 * sessions share. A monolithic shell would have had to swallow that tree and re-pass
 * PlanStudio's forty props. The studio asks `shells/index.ts` for slots; this file fills them.
 *
 * ⚠ **What is deliberately NOT faithful.** AutoCAD's own default UI theme is Dark and its model
 * space is dark too; ours is the plan canvas, which is white paper and stays white. Dark chrome
 * over a white model space is a real and common AutoCAD setup — it is what drafters who plot run
 * — but if the owner wants it the other way, Options ▸ Display ▸ Color theme flips the whole
 * shell to Light, and that switch is wired, not decorative.
 */
import * as React from "react";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import {
  ALL_COMMANDS, APP_MENU, BTN_BY_ID, FOLDED_W, LAYOUT_TABS, MENUS, OSNAP_MODES, PALETTES, QAT,
  QAT_MENU, QAT_OF, QAT_TICK, STATUS_ITEMS, TABS, TOOLBARS, WORKSPACES, panelWidth,
  resolveCommand, tabsFor,
  type AcadBtn, type AcadGroup, type AcadMenuItem, type AcadPanel,
} from "./catalog";
import { Icon } from "./icons";

// ── the handler contract ─────────────────────────────────────────────────────────────────────

export interface AcadHandler {
  /** What the command does. Absent (and no `select`/`slider`) ⇒ the item is grey. */
  run?: () => void;
  /** What it does when TYPED rather than clicked — an option prompt, where the button is a
   *  shortcut past it. ⚠ Only ZOOM needs this today; it is not a general escape hatch, and a
   *  control with a `typedRun` and no `run` would be grey, which is almost certainly wrong. */
  typedRun?: () => void;
  /** A tick, a bullet, or a lit toolbar button. */
  on?: boolean;
  /** Live but not right now. Used sparingly — see the note on `live` below. */
  enabled?: boolean;
  /** Why it is grey, or what it does here. One short sentence, shown in the tooltip. */
  note?: string;
  /** ⚠ This command ARMS A DRAWING TOOL, so the drawing needs the keyboard back — see
   *  `AcadCommandLine`. Without it, typing `L` then a length would send the length to the
   *  command line instead of to the tool that asked for it. */
  arms?: boolean;
  /** A live dropdown: the ribbon's Layer control. */
  select?: { value: any; options: { value: any; label: string }[]; onChange: (v: any) => void };
  /** A live slider: the underlay's Fade. */
  slider?: { value: number; min: number; max: number; step: number;
             onChange: (v: number) => void };
}

export type AcadHandlers = Record<string, AcadHandler | (() => void) | undefined>;

const norm = (h: AcadHandler | (() => void) | undefined): AcadHandler =>
  typeof h === "function" ? { run: h } : (h || {});

/** ⚠ `enabled` is used almost NOWHERE here, and that is deliberate. AutoCAD does not grey Move
 *  because nothing is selected — you press MOVE and it says "Select objects:". Greying on an
 *  empty selection would make grey mean two things at once ("not built" and "you have not
 *  clicked anything"), which is the mistake the SketchUp lane made first and had to undo. */
const live = (h: AcadHandler) => !!(h.run || h.select || h.slider) && h.enabled !== false;

// ── the two colour themes AutoCAD ships ──────────────────────────────────────────────────────

export interface AcadTheme {
  font: string; mono: string; size: number;
  title: string; tabs: string; ribbon: string; panel: string; sunk: string;
  line: string; edge: string; fg: string; soft: string;
  dim: string;             // the "not built" grey — the one colour here that carries meaning
  hover: string; hoverLine: string; press: string;
  menuBg: string; menuLine: string; sep: string;
  cmdBg: string; cmdFg: string; accent: string;
  /** ⚠ AutoCAD's MODEL SPACE background — the near-black behind a 3D view, and it is not the
   *  same grey as the ribbon. Used for the 3D viewport's loading state so the chunk arriving
   *  does not flash a different colour than the view it becomes. */
  canvas3d: string;
}

const FONT = '"Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
const MONO = 'Consolas, "Cascadia Mono", ui-monospace, monospace';

export const THEMES: Record<"dark" | "light", AcadTheme> = {
  dark: {
    font: FONT, mono: MONO, size: 12,
    title: "#2B2B2B", tabs: "#333333", ribbon: "#3B3B3B", panel: "#454545", sunk: "#2F2F2F",
    line: "#282828", edge: "#565656", fg: "#E2E2E2", soft: "#A9A9A9",
    dim: "#6E6E6E",
    hover: "#55606A", hoverLine: "#7C97AB", press: "#2E6DA4",
    menuBg: "#3B3B3B", menuLine: "#5A5A5A", sep: "#4E4E4E",
    cmdBg: "#232323", cmdFg: "#D2D6D9", accent: "#4E9BD1",
    canvas3d: "#33393F",
  },
  light: {
    font: FONT, mono: MONO, size: 12,
    title: "#E7EAEC", tabs: "#DCE0E3", ribbon: "#EFF1F3", panel: "#F7F8F9", sunk: "#E4E7E9",
    line: "#C8CCD0", edge: "#B4B9BE", fg: "#1F2429", soft: "#5A6169",
    dim: "#A6ACB2",
    hover: "#CCE4F7", hoverLine: "#7EB4DE", press: "#AFD5F2",
    menuBg: "#FFFFFF", menuLine: "#9AA0A6", sep: "#E2E5E8",
    cmdBg: "#FFFFFF", cmdFg: "#1F2429", accent: "#1B6FB5",
    canvas3d: "#D6DCE2",
  },
};

// ── what the chrome remembers ────────────────────────────────────────────────────────────────

export interface AcadCfg {
  theme: "dark" | "light";
  tab: string;
  menubar: boolean;
  ribbon: boolean;          // expanded, or minimised to the tab strip
  filetabs: boolean;
  layouttabs: boolean;
  cmdline: boolean;
  cmdRows: number;
  clean: boolean;           // CLEANSCREENON
  ucsicon: boolean;
  viewcube: boolean;
  navbar: boolean;
  toolbars: Record<string, boolean>;
  /** Which of the QAT's seven togglable commands are on it. ⚠ Keyed by the `qat.*` MENU id, not
   *  by the command it runs — the menu is what the user ticks, and `QAT_OF` maps one to the
   *  other in a single place so the tick and the button cannot disagree. */
  qat: Record<string, boolean>;
}

/** What the Plot dialog remembers. Every field here maps to a REAL query parameter on
 *  `/design/plans/<id>/export/` — none of it is decoration. `sheet`, `orientation` and `scale`
 *  go to `to_pdf`; the four content flags are its `furniture` / `dimensions` / `schedules` /
 *  `room_fill` arguments.
 *
 *  ⚠ Lives in `shell.tsx` beside the palette state, for the same reason: `handlers.ts` has to
 *  read it to answer PREVIEW, and the handler table is built before the chrome hook runs. */
export interface PlotCfg {
  sheet: string;
  orientation: "auto" | "landscape" | "portrait";
  fit: boolean;              // "Fit to paper" — the backend's scale="auto"
  scale: number;             // 1:N, used only when `fit` is off
  furniture: boolean;
  dimensions: boolean;
  schedules: boolean;
  colour: boolean;
}

// ⚠ Both lists are the SERVER'S, copied from `export.py` — `SHEETS` and `SCALES`. A paper size
// this shell offers and `to_pdf` does not know answers 400 with a stack trace in the detail.
export const PLOT_SHEETS = ["A4", "A3", "A2", "A1", "A0"];
export const PLOT_SCALES = [10, 20, 25, 50, 75, 100, 150, 200, 250, 500, 1000];

export const DEFAULT_PLOT: PlotCfg = {
  // ⚠ `colour: false` — a DRAWING is black on white. The dark screen palette exists because
  // it is easier to draw against for eight hours; nobody plots it, and a colour sheet costs
  // more to print and reads worse on site.
  sheet: "A3", orientation: "auto", fit: true, scale: 50,
  furniture: true, dimensions: true, schedules: true, colour: false,
};

/** ⚠⚠ **WHICH PALETTES ARE DOCKED IS NOT IN HERE, ON PURPOSE.** A palette is open exactly when
 *  its handler says `on`, so the column is a view of the handler table like everything else —
 *  and a palette whose panel does not exist cannot be opened by a stale saved blob. The state
 *  itself lives one level up, in `shell.tsx`, because `handlers.ts` has to read it to answer
 *  `on` and the handler table is built before this hook runs. */

const CFG_KEY = "pderp.design.acad.ui";

const DEFAULT_CFG: AcadCfg = {
  theme: "dark",
  tab: "home",
  // ⚠ OFF, because AutoCAD's is: MENUBAR has shipped at 0 since the ribbon arrived in 2009.
  // Turning it on is one tick in the QAT's ▾ menu, exactly where AutoCAD keeps it.
  menubar: false,
  ribbon: true,
  filetabs: true,
  layouttabs: true,
  cmdline: true,
  cmdRows: 3,
  clean: false,
  ucsicon: true,
  viewcube: true,
  navbar: true,
  toolbars: Object.fromEntries(TOOLBARS.map((t) => [t.id, t.on])),
  // ⚠ Save As is OFF, exactly as AutoCAD ships — the other six are on. Getting this wrong is
  // invisible but wrong: a drafter who knows the default QAT would see a button that is not
  // supposed to be there and trust the rest of the transcription less.
  qat: { "qat.new": true, "qat.open": true, "qat.save": true, "qat.saveas": false,
         "qat.plot": true, "qat.undo": true, "qat.redo": true },
};

export interface AcadChrome {
  all: AcadHandlers;
  ui: AcadTheme;
  cfg: AcadCfg;
  set: (patch: Partial<AcadCfg>) => void;
  setTb: (id: string, on: boolean) => void;
  openMenu: string | null;
  setOpenMenu: (v: string | null) => void;
  appMenu: boolean;
  setAppMenu: (v: boolean) => void;
  options: boolean;
  setOptions: (v: boolean) => void;
  /** The command line's transcript, oldest first. */
  hist: string[];
  say: (line: string) => void;
  /** Run a command by id — from the ribbon, a menu, a toolbar or the command line. */
  fire: (id: string, typed?: string) => boolean;
  /** What Enter on an empty command line repeats. */
  lastId: string | null;
  /** Set by the command line so `fire` can hand the keyboard back to the drawing. */
  releaseKeyboard: React.MutableRefObject<(() => void) | null>;
  /** ⚠⚠ **THE OTHER DIRECTION, AND IT IS HOW AutoCAD IS ACTUALLY DRIVEN.** A drafter types
   *  `L ⏎` with the mouse over the drawing and never touches the command box — the box has
   *  focus in AutoCAD essentially always. Here the DRAWING owns the keyboard (see the note on
   *  `AcadCommandLine`), so a letter typed at the canvas reached nothing at all and every
   *  command had to be clicked. `shell.tsx` calls this with the letter when, and only when,
   *  nothing is running; the command line focuses itself and takes it from there. */
  takeKeyboard: React.MutableRefObject<((ch: string) => void) | null>;
  /** ⚠⚠ **A LINE THAT STARTS WITH `(` IS LISP, and AutoCAD has taken it at the command prompt
   *  since 1986.** The chrome cannot evaluate it — the interpreter needs the drawing, the
   *  commands and the sysvars, all of which live in the studio — so it hands it over, the same
   *  way `takeKeyboard` hands a letter the other way. */
  evalLisp: React.MutableRefObject<((src: string) => void) | null>;
  /** A name `resolveCommand` does not know. ⚠ Answers whether it RAN: a `.lsp` file that
   *  defines `(defun c:BEAM …)` adds a command to this app, and *Unknown command "BEAM"* would
   *  be a lie the moment the file is loaded. Also where APPLOAD and LOAD are handled. */
  lispCmd: React.MutableRefObject<((name: string) => boolean) | null>;
}

/** Everything the AutoCAD chrome remembers, plus the merged handler table. */
export function useAutoCadChrome(handlers: AcadHandlers): AcadChrome {
  const [cfg, setCfg] = useState<AcadCfg>(() => {
    try {
      const raw = localStorage.getItem(CFG_KEY);
      // ⚠ Merged over the defaults, never used raw: a blob saved before a toolbar or a palette
      // existed would otherwise leave that one permanently `undefined`, i.e. silently off
      // forever, and nothing would ever say so. The SketchUp lane learned this on its toolbars.
      if (raw) {
        const v = JSON.parse(raw);
        return { ...DEFAULT_CFG, ...v,
                 toolbars: { ...DEFAULT_CFG.toolbars, ...(v.toolbars || {}) } };
      }
    } catch { /* a corrupt value must not stop the studio opening */ }
    return DEFAULT_CFG;
  });
  const [openMenu, setOpenMenu] = useState<string | null>(null);
  const [appMenu, setAppMenu] = useState(false);
  const [options, setOptions] = useState(false);
  const [hist, setHist] = useState<string[]>([
    // ⚠ NOT "AutoCAD". The chrome is modelled on it; the banner is where a user reads what
    // program they are in, and this one is pdcad.
    "pdcad — Drafting & Annotation",
    "Command:",
  ]);
  const [lastId, setLastId] = useState<string | null>(null);
  const releaseKeyboard = useRef<(() => void) | null>(null);
  const takeKeyboard = useRef<((ch: string) => void) | null>(null);
  const evalLisp = useRef<((src: string) => void) | null>(null);
  const lispCmd = useRef<((name: string) => boolean) | null>(null);

  const set = (patch: Partial<AcadCfg>) => setCfg((c) => {
    const n = { ...c, ...patch };
    try { localStorage.setItem(CFG_KEY, JSON.stringify(n)); } catch { /* private mode */ }
    return n;
  });
  const setTb = (id: string, on: boolean) => set({ toolbars: { ...cfg.toolbars, [id]: on } });

  // ⚠ Bounded, and not by taste: the transcript is a React state array that grows on every
  // click for as long as the studio is open. 400 lines is more scrollback than AutoCAD's own
  // default and cheap to hold; unbounded, a long session's re-render cost climbs with it.
  const say = (line: string) =>
    setHist((h) => (h.length > 400 ? [...h.slice(-380), line] : [...h, line]));

  // The chrome's OWN commands — the ones that act on the interface rather than the drawing.
  // Merged UNDER the page's, so `handlers.ts` can override any of them.
  const own: AcadHandlers = useMemo(() => ({
    "ui.menubar": { run: () => set({ menubar: !cfg.menubar }), on: cfg.menubar },
    "ui.filetabs": { run: () => set({ filetabs: !cfg.filetabs }), on: cfg.filetabs },
    "ui.layouttabs": { run: () => set({ layouttabs: !cfg.layouttabs }), on: cfg.layouttabs },
    "ui.cmdline": { run: () => set({ cmdline: !cfg.cmdline }), on: cfg.cmdline },
    "ui.cleanscreen": { run: () => set({ clean: !cfg.clean }), on: cfg.clean },
    "ui.toolbars": { run: () => setOptions(true), note: "Options ▸ Display lists them" },
    // ⚠⚠ **THE QAT'S SEVEN TICKS, WHICH ARE THE ONLY CUSTOMISATION THIS SHELL OFFERS.** They
    // were grey with the reason "the Quick Access Toolbar is fixed" — and it was, but there was
    // no reason for it to be: which of seven known buttons sit on a toolbar is a `Record<string,
    // boolean>` in the chrome config that already persists, not a customisation system. What
    // stays grey is More Commands, which IS one.
    ...Object.fromEntries(Object.keys(QAT_OF).map((id) => [id, {
      run: () => set({ qat: { ...cfg.qat, [id]: !cfg.qat[id] } }),
      on: !!cfg.qat[id],
      note: `shows or hides ${BTN_BY_ID[QAT_OF[id]]?.label || QAT_OF[id]} on the Quick Access `
            + "Toolbar",
    } as AcadHandler])),
    // ⚠ Owned HERE, not in `handlers.ts` — that table is merged over this one, so a second
    // definition there would replace this and the dialog could never open. See the note in
    // `handlers.ts` where it used to be.
    "app.options": { run: () => setOptions(true),
                     note: "Display, Toolbars — and the way back to another interface" },
    "view.ucsicon": { run: () => set({ ucsicon: !cfg.ucsicon }), on: cfg.ucsicon },
    "view.viewcube": { run: () => set({ viewcube: !cfg.viewcube }), on: cfg.viewcube },
    "view.navbar": { run: () => set({ navbar: !cfg.navbar }), on: cfg.navbar },
    "ws.drafting": { on: true, note: "the only workspace this shell builds" },
    ...Object.fromEntries(TOOLBARS.map((t) => [t.id, {
      run: () => setTb(t.id, !cfg.toolbars[t.id]), on: !!cfg.toolbars[t.id],
    }])),
    ...handlers,
  }), [handlers, cfg]);

  /** ⚠ THE ONE PLACE A COMMAND RUNS. Ribbon, menu, toolbar and command line all come through
   *  here, so every command a person issues is echoed into the transcript the way AutoCAD
   *  echoes it — which is also how you can see, after the fact, that the button you pressed
   *  did nothing because it was grey. Returns whether it ran. */
  const fire = (id: string, typed?: string): boolean => {
    const h = norm(own[id]);
    const b = BTN_BY_ID[id];
    const name = typed || (b?.cmd ? `_${b.cmd}` : b?.label || id);
    if (!live(h)) {
      say(`${name}  ·  ${h.note ? h.note : "not built yet"}`);
      return false;
    }
    say(name);
    setLastId(id);
    // ⚠⚠ **A TYPED COMMAND AND A CLICKED BUTTON ARE TWO DIFFERENT GESTURES.** `typed` is only
    // ever passed by the command line, so it is the flag as well as the echo. ZOOM is the case
    // that needs it: the ribbon's Zoom Extents is one click that means one thing, and `ZOOM ⏎`
    // is the start of a sentence — `[All/Extents/Previous/Scale/Window]`. Making the button ask
    // would be a click that used to work needing two.
    if (typed !== undefined && h.typedRun) h.typedRun();
    else h.run?.();
    // ⚠ Hand the keyboard back to the DRAWING when the command armed a tool. The studio's typed
    // lengths and angles are read by the canvas's own key handler, which never sees a keystroke
    // while an <input> holds focus — so `L` followed by `3000 ⏎` would put the 3000 in the
    // command line and draw a wall of whatever length the mouse happened to be at.
    if (h.arms) releaseKeyboard.current?.();
    return true;
  };

  return { all: own, ui: THEMES[cfg.theme] || THEMES.dark, cfg, set, setTb,
           openMenu, setOpenMenu, appMenu, setAppMenu, options, setOptions,
           hist, say, fire, lastId, releaseKeyboard, takeKeyboard, evalLisp, lispCmd };
}

// ── tooltips ─────────────────────────────────────────────────────────────────────────────────

/** AutoCAD's rich tooltip is three lines: the name, the command, then what it does. The native
 *  `title` attribute honours newlines, which is enough and costs no layout. */
function tip(b: AcadBtn | undefined, h: AcadHandler, label?: string): string {
  const name = label || b?.label?.replace(/\n/g, " ") || "";
  const cmd = b?.cmd ? `\n${b.cmd}${b.key && b.key !== b.cmd ? `  (${b.key})` : ""}` : "";
  if (!live(h)) return `${name}${cmd}\n⚠ ${h.note || "not built yet"}`;
  return `${name}${cmd}${h.note ? `\n${h.note}` : b?.hint ? `\n${b.hint}` : ""}`;
}

// ── a ribbon / toolbar button ────────────────────────────────────────────────────────────────

function Btn({ b, chrome, big, active, size }: {
  b: AcadBtn; chrome: AcadChrome; big?: boolean; active?: boolean; size?: number;
}) {
  const { ui, all } = chrome;
  const h = norm(all[b.id]);
  const on = live(h);
  const [hover, setHover] = useState(false);
  const [openFly, setOpenFly] = useState(false);
  const px = size || (big ? 32 : 16);
  const down = active || h.on;
  const hasFly = !!b.flyout?.length;
  // ⚠ A flyout is reachable if ANY of its rows is live, even when its parent is not. Greying the
  // arrow because the parent has no handler would bury working commands behind a dead one.
  const flyLive = hasFly && b.flyout!.some((c) => live(norm(all[c.id])));

  const face = (
    <>
      <span style={{ opacity: on ? 1 : 0.34, lineHeight: 0 }}><Icon id={b.id} size={px} /></span>
      {big ? (
        <span style={{ marginTop: 3, fontSize: 11, lineHeight: 1.15, textAlign: "center",
                       whiteSpace: "pre-line", color: on ? ui.fg : ui.dim }}>
          {b.label}</span>) : null}
    </>
  );

  return (
    <span style={{ position: "relative", display: "inline-flex", flex: "0 0 auto" }}
      onMouseLeave={() => { setHover(false); setOpenFly(false); }}>
      <span style={{ display: "flex", flexDirection: big ? "column" : "row",
                     alignItems: "center" }}>
        <button
          data-testid={`acad-cmd-${b.id}`}
          data-acad-off={on ? undefined : "1"}
          // ⚠⚠ **`data-acad-on` TOO — the status bar has had it for a while and the ribbon has
          // not, so a probe checking whether VPORTS, UFRAME or Layer Properties was TOGGLED had
          // to read a computed background colour and guess. Half the toggles in this shell are
          // on the ribbon. "Is it available" and "is the thing it controls switched on" are
          // different questions and both need an answer in the DOM.
          data-acad-on={down ? "1" : undefined}
          // ⚠ The typed NAME, so a test can pick a grey command out of the DOM at runtime
          // rather than hardcoding one. Three e2e failures so far have been a test naming a
          // command that later got built — DIMALIGNED, DIMANGULAR, and now MTEXT.
          data-acad-cmd={b.cmd || undefined}
          disabled={!on}
          title={tip(b, h)}
          onClick={() => chrome.fire(b.id)}
          onMouseEnter={() => setHover(true)}
          style={{
            display: "flex", flexDirection: "column", alignItems: "center",
            justifyContent: "center", gap: 0,
            width: big ? 62 : px + 8, minHeight: big ? 56 : px + 6, padding: big ? "4px 2px" : 3,
            cursor: on ? "pointer" : "default", color: on ? ui.fg : ui.dim,
            font: `${ui.size}px ${ui.font}`,
            background: down ? ui.press : hover && on ? ui.hover : "transparent",
            border: `1px solid ${down || (hover && on) ? ui.hoverLine : "transparent"}`,
            borderRadius: 2,
          }}>
          {face}
        </button>
        {hasFly ? (
          <button
            data-testid={`acad-fly-${b.id}`}
            disabled={!flyLive}
            title={flyLive ? "" : "nothing in this list is built yet"}
            onClick={() => setOpenFly((v) => !v)}
            onMouseEnter={() => setHover(true)}
            style={{ width: big ? 62 : 11, height: big ? 12 : px + 6, padding: 0, lineHeight: 1,
                     fontSize: 8, cursor: flyLive ? "pointer" : "default",
                     color: flyLive ? ui.soft : ui.dim,
                     background: openFly ? ui.hover : hover ? ui.hover : "transparent",
                     border: `1px solid ${openFly || hover ? ui.hoverLine : "transparent"}`,
                     borderRadius: 2 }}>▾</button>) : null}
      </span>
      {hasFly && openFly ? (
        <div data-testid={`acad-flyout-${b.id}`}
          style={{ position: "absolute", top: "100%", left: 0, zIndex: 950, minWidth: 210,
                   background: ui.menuBg, border: `1px solid ${ui.menuLine}`,
                   boxShadow: "2px 3px 8px rgba(0,0,0,0.35)", padding: "2px 0",
                   font: `${ui.size}px ${ui.font}`, color: ui.fg }}>
          {b.flyout!.map((c, i) => {
            const ch = norm(all[c.id]);
            const cOn = live(ch);
            return (
              <div key={`${c.id}${i}`} data-testid={`acad-mi-${c.id}`}
                data-acad-off={cOn ? undefined : "1"}
                title={tip(c, ch)}
                onClick={() => { if (cOn) { chrome.fire(c.id); setOpenFly(false); } }}
                style={{ display: "flex", alignItems: "center", gap: 7, height: 24,
                         padding: "0 10px", cursor: cOn ? "pointer" : "default",
                         color: cOn ? ui.fg : ui.dim }}
                onMouseEnter={(e) => {
                  if (cOn) e.currentTarget.style.background = chrome.ui.hover; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
                <span style={{ opacity: cOn ? 1 : 0.34, lineHeight: 0 }}>
                  <Icon id={c.id} size={16} /></span>
                <span style={{ flex: 1, whiteSpace: "nowrap" }}>{c.label}</span>
                {c.cmd ? <span style={{ color: ui.dim, fontFamily: ui.mono, fontSize: 10.5 }}>
                  {c.cmd}</span> : null}
              </div>);
          })}
        </div>) : null}
    </span>);
}

/** A live dropdown or slider — the ribbon's Layer picker, the underlay's Fade. */
function Ctl({ b, chrome }: { b: AcadBtn; chrome: AcadChrome }) {
  const { ui, all } = chrome;
  const h = norm(all[b.id]);
  const on = live(h);
  const w = b.width || 150;
  const shared: React.CSSProperties = {
    width: w, height: 21, font: `${ui.size}px ${ui.font}`,
    background: on ? ui.sunk : "transparent", color: on ? ui.fg : ui.dim,
    border: `1px solid ${ui.edge}`, borderRadius: 2, padding: "0 4px",
  };
  return (
    <span data-testid={`acad-ctl-${b.id}`} data-acad-off={on ? undefined : "1"}
      title={tip(b, h)}
      style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "1px 3px" }}>
      <span style={{ opacity: on ? 1 : 0.34, lineHeight: 0 }}><Icon id={b.id} size={16} /></span>
      {h.slider ? (
        <input type="range" disabled={!on} style={{ ...shared, padding: 0 }}
          min={h.slider.min} max={h.slider.max} step={h.slider.step} value={h.slider.value}
          onChange={(e) => h.slider!.onChange(Number(e.target.value))} />
      ) : (
        <select disabled={!on} style={shared} value={h.select ? String(h.select.value ?? "") : ""}
          onChange={(e) => h.select?.onChange(e.target.value)}>
          {h.select
            ? h.select.options.map((o) => (
                <option key={String(o.value)} value={String(o.value)}>{o.label}</option>))
            : <option>{b.label}</option>}
        </select>)}
    </span>);
}

// ── the ribbon ───────────────────────────────────────────────────────────────────────────────

/** ⚠ `labels` is off in a docked panel and on in a slide-out, which is AutoCAD's own rule and
 *  not a space-saving compromise: a small button in the ribbon proper is icon-only, and the
 *  written name appears in the slide-out, the flyout and the tooltip. Labelling them in place
 *  looked reasonable and made the Home tab about a thousand pixels wider than the screen, so
 *  the Clipboard and View panels were only reachable by scrolling the ribbon sideways — which
 *  AutoCAD never asks anyone to do. */
function Groups({ groups, chrome, active, labels }: {
  groups: AcadGroup[]; chrome: AcadChrome; active: string | null; labels?: boolean;
}) {
  return (<>{groups.map((g, i) => {
    if ("gap" in g) {
      return <span key={i} style={{ width: 1, alignSelf: "stretch", margin: "4px 3px",
                                    background: chrome.ui.line }} />;
    }
    if ("big" in g) {
      return <Btn key={i} b={g.big} chrome={chrome} big active={active === g.big.id} />;
    }
    if ("wide" in g) return <Ctl key={i} b={g.wide} chrome={chrome} />;
    if ("stack" in g) {
      return (
        <span key={i} style={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {g.stack.map((b) => <Ctl key={b.id} b={b} chrome={chrome} />)}
        </span>);
    }
    return (
      <span key={i} style={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {g.col.map((b) => (b.control
          ? <Ctl key={b.id} b={b} chrome={chrome} />
          : labels
            ? (<span key={b.id} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <Btn b={b} chrome={chrome} active={active === b.id} />
                <span style={{ fontSize: 11, whiteSpace: "nowrap", maxWidth: 168,
                               overflow: "hidden", textOverflow: "ellipsis",
                               color: live(norm(chrome.all[b.id])) ? chrome.ui.fg
                                                                   : chrome.ui.dim }}>
                  {b.label}</span>
              </span>)
            : <Btn key={b.id} b={b} chrome={chrome} active={active === b.id} />))}
      </span>);
  })}</>);
}

function Panel({ p, chrome, active }: {
  p: AcadPanel; chrome: AcadChrome; active: string | null;
}) {
  const { ui } = chrome;
  const [open, setOpen] = useState(false);
  const lh = p.launcher ? norm(chrome.all[p.launcher.id]) : null;

  return (
    <div data-testid={`acad-panel-${p.id}`}
      // ⚠ `relative` so the slide-out drops under THIS panel. Without it the nearest positioned
      // ancestor is the whole ribbon row, and the slide-out lands under the ribbon at whatever
      // x the static position happened to give it — right on a wide screen, wrong on a narrow one.
      style={{ position: "relative", display: "flex", flexDirection: "column", flex: "0 0 auto",
               borderRight: `1px solid ${ui.line}`, background: ui.panel }}
      onMouseLeave={() => setOpen(false)}>
      <div style={{ flex: 1, display: "flex", alignItems: "flex-start", gap: 2,
                    padding: "4px 6px" }}>
        <Groups groups={p.groups} chrome={chrome} active={active} />
      </div>
      <div style={{ position: "relative", height: 17, display: "flex", alignItems: "center",
                    justifyContent: "center", gap: 4, padding: "0 6px", fontSize: 11,
                    color: ui.soft, background: ui.ribbon, borderTop: `1px solid ${ui.line}`,
                    cursor: p.more ? "pointer" : "default" }}
        onClick={() => p.more && setOpen((v) => !v)}>
        <span>{p.label}</span>
        {p.more ? <span style={{ fontSize: 7 }}>▼</span> : null}
        {p.launcher ? (
          <span data-testid={`acad-launcher-${p.launcher.id}`}
            data-acad-off={lh && live(lh) ? undefined : "1"}
            title={tip(p.launcher, lh || {})}
            onClick={(e) => { e.stopPropagation(); chrome.fire(p.launcher!.id); }}
            style={{ position: "absolute", right: 4, fontSize: 10,
                     color: lh && live(lh) ? ui.accent : ui.dim,
                     cursor: lh && live(lh) ? "pointer" : "default" }}>⌄</span>) : null}
      </div>
      {open && p.more ? (
        <div style={{ position: "absolute", top: "100%", left: 0, zIndex: 945,
                      background: ui.panel, border: `1px solid ${ui.menuLine}`,
                      boxShadow: "2px 3px 8px rgba(0,0,0,0.35)", padding: "5px 6px",
                      display: "flex", gap: 6, alignItems: "flex-start" }}>
          <Groups groups={p.more} chrome={chrome} active={active} labels />
        </div>) : null}
    </div>);
}

/** A panel folded to one button, which is what AutoCAD does when the ribbon runs out of room. */
function FoldedPanel({ p, chrome, active }: {
  p: AcadPanel; chrome: AcadChrome; active: string | null;
}) {
  const { ui } = chrome;
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`acad-folded-${p.id}`}
      onMouseLeave={() => setOpen(false)}
      style={{ position: "relative", width: FOLDED_W, flex: "0 0 auto", display: "flex",
               flexDirection: "column", background: ui.panel,
               borderRight: `1px solid ${ui.line}` }}>
      <div onClick={() => setOpen((v) => !v)}
        style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
                 justifyContent: "center", gap: 2, cursor: "pointer",
                 background: open ? ui.hover : "transparent" }}>
        <Icon id={p.groups.length ? firstId(p) : p.id} size={26} />
        <span style={{ fontSize: 10.5, textAlign: "center", lineHeight: 1.1, color: ui.fg }}>
          {p.label}</span>
      </div>
      <div style={{ height: 17, display: "flex", alignItems: "center", justifyContent: "center",
                    gap: 4, fontSize: 11, color: ui.soft, background: ui.ribbon,
                    borderTop: `1px solid ${ui.line}` }}>
        <span style={{ fontSize: 7 }}>▼</span>
      </div>
      {open ? (
        <div style={{ position: "absolute", top: "100%", left: 0, zIndex: 946,
                      background: ui.panel, border: `1px solid ${ui.menuLine}`,
                      boxShadow: "2px 3px 8px rgba(0,0,0,0.35)", padding: "5px 6px",
                      display: "flex", gap: 6, alignItems: "flex-start" }}>
          <Groups groups={p.groups} chrome={chrome} active={active} />
          {p.more ? <Groups groups={p.more} chrome={chrome} active={active} labels /> : null}
        </div>) : null}
    </div>);
}

/** The first command in a panel, so a folded panel can wear an icon that means something. */
const firstId = (p: AcadPanel): string => {
  for (const g of p.groups) {
    if ("big" in g) return g.big.id;
    if ("wide" in g) return g.wide.id;
    if ("stack" in g && g.stack.length) return g.stack[0].id;
    if ("col" in g && g.col.length) return g.col[0].id;
  }
  return p.id;
};

export function AcadRibbon({ chrome, active, ws }: {
  chrome: AcadChrome; active: string | null; ws: string;
}) {
  // ⚠ What a ribbon render costs, for the same reason `Studio` carries one: a typed command is
  // seven UI round trips and the ribbon is the biggest DOM tree in the app. Two `performance.now`
  // a render; nothing in the app reads it — `/var/tmp/pacecheck.py` does.
  const t0 = performance.now();
  useLayoutEffect(() => {
    const w = window as unknown as Record<string, unknown>;
    const r = (w.__pdcadRibbon ||= { n: 0, ms: 0 }) as { n: number; ms: number };
    r.n += 1; r.ms += performance.now() - t0;
  });
  const { ui, cfg } = chrome;
  // ⚠⚠ **THE WORKSPACE CHOOSES THE TABS, AND THE FALLBACK IS WHY THIS IS TWO LINES.** Switching
  // to a workspace that does not contain the open tab (Drafting → 3D Basics with Annotate up)
  // would otherwise leave `cfg.tab` naming a tab that is no longer on screen, and the ribbon
  // would render the FIRST tab's panels under a strip where nothing looks selected.
  //
  // ⚠ `ws` is a PROP, not a field of `cfg`, and that is deliberate. `cfg` lives inside
  // `useAutoCadChrome`, which runs AFTER the handler table is built — so a handler asking "am I
  // the current workspace?" would read a value one render stale and the radio tick in Tools ▸
  // Workspaces would lag a click behind. The palettes solved the same ordering problem the same
  // way; see `PAL_KEY` in `shell.tsx`.
  const tabs = tabsFor(ws);
  const tab = tabs.find((t) => t.id === cfg.tab) || tabs[0];

  // ⚠⚠ **AUTOCAD COLLAPSES, IT DOES NOT SCROLL.** Ten Home panels are 1 800 px wide and a ribbon
  // with a horizontal scrollbar is not a ribbon — Groups, Utilities, Clipboard and View were
  // simply off the right-hand edge, present in the DOM and unreachable, which is the exact
  // failure this whole mode exists to avoid. AutoCAD folds panels from the RIGHT into a single
  // button that drops the panel on click, so the leading panels — the ones a drafter uses every
  // minute — always stay open.
  // ⚠ One ResizeObserver on the row and pure arithmetic from `panelWidth`. No per-panel
  // measurement, no layout thrash, no second render pass on every tab change.
  const row = useRef<HTMLDivElement | null>(null);
  const [avail, setAvail] = useState(0);
  useEffect(() => {
    const el = row.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver((e) => setAvail(e[0].contentRect.width));
    ro.observe(el);
    setAvail(el.clientWidth);
    return () => ro.disconnect();
  }, [cfg.ribbon]);

  const widths = tab.panels.map(panelWidth);
  let fold = 0;
  if (avail > 0) {
    const fits = (n: number) =>
      widths.slice(0, widths.length - n).reduce((a, b) => a + b, 0) + n * FOLDED_W <= avail;
    while (fold < tab.panels.length && !fits(fold)) fold++;
  }
  const openTo = tab.panels.length - fold;
  return (
    <div data-testid="acad-ribbon" style={{ flex: "0 0 auto", background: ui.ribbon,
                                            borderBottom: `1px solid ${ui.line}`,
                                            font: `${ui.size}px ${ui.font}`, color: ui.fg }}>
      <div style={{ display: "flex", alignItems: "stretch", height: 23, background: ui.tabs,
                    userSelect: "none" }}>
        {tabs.map((t) => (
          <div key={t.id} data-testid={`acad-tab-${t.id}`}
            onClick={() => chrome.set({ tab: t.id, ribbon: cfg.tab === t.id ? !cfg.ribbon : true })}
            style={{ padding: "0 11px", display: "flex", alignItems: "center", cursor: "pointer",
                     color: cfg.tab === t.id ? ui.fg : ui.soft,
                     background: cfg.tab === t.id && cfg.ribbon ? ui.ribbon : "transparent",
                     borderTop: `2px solid ${cfg.tab === t.id ? ui.accent : "transparent"}` }}>
            {t.label}</div>))}
        <span style={{ flex: 1 }} />
        <div data-testid="acad-ribbon-min" title={cfg.ribbon ? "Minimize the ribbon" : "Show the full ribbon"}
          onClick={() => chrome.set({ ribbon: !cfg.ribbon })}
          style={{ padding: "0 9px", display: "flex", alignItems: "center", cursor: "pointer",
                   color: ui.soft, fontSize: 10 }}>{cfg.ribbon ? "▲" : "▼"}</div>
      </div>
      {cfg.ribbon ? (
        <div ref={row} style={{ display: "flex", alignItems: "stretch", overflow: "hidden",
                                minHeight: 96, position: "relative" }}>
          {tab.panels.map((p, i) => (i < openTo
            ? <Panel key={p.id} p={p} chrome={chrome} active={active} />
            : <FoldedPanel key={p.id} p={p} chrome={chrome} active={active} />))}
        </div>) : null}
    </div>);
}

// ── the pull-down menus ──────────────────────────────────────────────────────────────────────

function MenuItems({ items, chrome, onRun, depth = 0 }: {
  items: AcadMenuItem[]; chrome: AcadChrome; onRun: () => void; depth?: number;
}) {
  const { ui, all } = chrome;
  const [openSub, setOpenSub] = useState<string | null>(null);
  const [subTop, setSubTop] = useState(0);

  return (
    <div style={{
      position: depth ? "absolute" : "relative",
      left: depth ? "100%" : undefined, top: depth ? subTop : undefined,
      minWidth: 224, background: ui.menuBg, border: `1px solid ${ui.menuLine}`,
      boxShadow: "2px 3px 8px rgba(0,0,0,0.35)", padding: "2px 0", zIndex: 940 + depth,
      font: `${ui.size}px ${ui.font}`, color: ui.fg, userSelect: "none",
    }}>
      {items.map((it, i) => {
        if (it.sep) {
          return <div key={`s${i}`} style={{ height: 1, background: ui.sep,
                                             margin: "3px 0 3px 26px" }} />;
        }
        const h = norm(all[it.id || ""]);
        const hasSub = !!it.sub?.length;
        // ⚠ A submenu is live if ANY of its children is. Greying a parent for having no handler
        // of its own would bury working commands — Draw ▸ Circle has no command, its rows do.
        const subLive = hasSub && it.sub!.some((c) => !c.sep && live(norm(all[c.id || ""])));
        const on = live(h) || subLive;
        const open = openSub === it.id;

        return (
          <div key={it.id ? `${it.id}${i}` : i} style={{ position: "relative" }}
            onMouseEnter={(e) => {
              if (hasSub && on) {
                setOpenSub(it.id!);
                setSubTop((e.currentTarget as HTMLElement).offsetTop - 3);
              } else setOpenSub(null);
            }}>
            <div
              data-testid={it.id ? `acad-mi-${it.id}` : undefined}
              data-acad-off={on ? undefined : "1"}
              title={on ? "" : `⚠ ${h.note || "not built yet"}`}
              onClick={() => { if (on && !hasSub) { chrome.fire(it.id!); onRun(); } }}
              style={{ display: "flex", alignItems: "center", height: 22, padding: "0 8px 0 0",
                       cursor: "default", color: on ? ui.fg : ui.dim,
                       background: open ? ui.hover : "transparent" }}
              onMouseEnter={(e) => { if (on && !hasSub) e.currentTarget.style.background = ui.hover; }}
              onMouseLeave={(e) => { if (!open) e.currentTarget.style.background = "transparent"; }}>
              <span style={{ width: 26, flex: "0 0 auto", textAlign: "center",
                             fontSize: it.radio ? 15 : 12, lineHeight: "22px" }}>
                {it.toggle && h.on ? "✓" : it.radio && h.on ? "•" : ""}</span>
              <span style={{ flex: 1, whiteSpace: "nowrap" }}>{it.label}</span>
              {it.key ? <span style={{ marginLeft: 26, whiteSpace: "nowrap",
                                       color: on ? ui.soft : ui.dim }}>{it.key}</span> : null}
              {hasSub ? <span style={{ marginLeft: 14, fontSize: 10 }}>▶</span> : null}
            </div>
            {hasSub && open ? (
              <MenuItems items={it.sub!} chrome={chrome} onRun={onRun} depth={depth + 1} />
            ) : null}
          </div>);
      })}
    </div>);
}

export function AcadMenuBar({ chrome }: { chrome: AcadChrome }) {
  const { ui, openMenu, setOpenMenu } = chrome;
  const root = useRef<HTMLDivElement | null>(null);

  // ⚠ A click anywhere else closes the open menu, and Esc does too — the two habits a menu bar
  // must honour or it feels stuck. Capture phase, because the canvas swallows plain clicks.
  useEffect(() => {
    if (!openMenu) return;
    const off = (e: MouseEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpenMenu(null);
    };
    const esc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpenMenu(null); };
    window.addEventListener("mousedown", off, true);
    window.addEventListener("keydown", esc, true);
    return () => {
      window.removeEventListener("mousedown", off, true);
      window.removeEventListener("keydown", esc, true);
    };
  }, [openMenu, setOpenMenu]);

  return (
    <div ref={root} data-testid="acad-menubar"
      style={{ height: 22, flex: "0 0 auto", display: "flex", alignItems: "stretch",
               background: ui.tabs, borderBottom: `1px solid ${ui.line}`, position: "relative",
               zIndex: 930, font: `${ui.size}px ${ui.font}`, color: ui.fg, userSelect: "none" }}>
      {MENUS.map((m) => (
        <div key={m.label} style={{ position: "relative" }}>
          <div data-testid={`acad-menu-${m.label.toLowerCase()}`}
            onClick={() => setOpenMenu(openMenu === m.label ? null : m.label)}
            // ⚠ Once one menu is open, SLIDING sideways opens the next without a second click.
            // Every desktop menu bar since 1984 does this; its absence is felt immediately.
            onMouseEnter={() => { if (openMenu) setOpenMenu(m.label); }}
            style={{ padding: "0 9px", display: "flex", alignItems: "center", cursor: "default",
                     background: openMenu === m.label ? ui.hover : "transparent" }}>
            {m.label}</div>
          {openMenu === m.label ? (
            <div style={{ position: "absolute", top: "100%", left: 0 }}>
              <MenuItems items={m.items} chrome={chrome} onRun={() => setOpenMenu(null)} />
            </div>) : null}
        </div>))}
    </div>);
}

// ── the title bar: the big A, the QAT, the drawing name, the workspace ───────────────────────

/** What each workspace is called, in AutoCAD's own words. Exported because `handlers.ts` says
 *  the same names on the status bar, and two spellings of "Drafting & Annotation" is one too
 *  many for a label whose entire job is to be recognised. */
export const WS_NAME: Record<string, string> = {
  "ws.drafting": "Drafting & Annotation",
  "ws.3dbasics": "3D Basics",
  "ws.3dmodel": "3D Modeling",
};

export function AcadTitleBar({ chrome, title, dirty, ws, user, onUser }: {
  chrome: AcadChrome; title: string; dirty?: boolean; ws: string;
  /** Who is signed in. ⚠ AutoCAD puts the account here and so does pdcad — it is the one place
   *  a person looks to find out whose session they are in on a shared workstation. */
  user?: string | null;
  onUser?: () => void;
}) {
  const { ui } = chrome;
  const [qatMenu, setQatMenu] = useState(false);
  const [wsMenu, setWsMenu] = useState(false);

  return (
    <div data-testid="acad-titlebar"
      style={{ height: 27, flex: "0 0 auto", display: "flex", alignItems: "center", gap: 2,
               padding: "0 6px 0 0", background: ui.title, borderBottom: `1px solid ${ui.line}`,
               font: `${ui.size}px ${ui.font}`, color: ui.fg, position: "relative",
               zIndex: 935, userSelect: "none" }}>
      {/* the Application button — AutoCAD's big red A, and the way to Options */}
      <div data-testid="acad-appbtn" title="Application menu"
        onClick={() => chrome.setAppMenu(!chrome.appMenu)}
        style={{ width: 34, height: 27, display: "grid", placeItems: "center", cursor: "pointer",
                 background: chrome.appMenu ? "#9E1B32" : "#C8102E", color: "#fff",
                 fontWeight: 700, fontSize: 15, letterSpacing: -0.5 }}>A</div>

      <div data-testid="acad-qat" style={{ display: "flex", alignItems: "center", gap: 1,
                                           padding: "0 4px" }}>
        {/* ⚠⚠ **FILTERED THROUGH THE TICKS, AND THE SEPARATORS ARE FILTERED WITH IT.** Dropping
            a button without dropping its separator leaves two rules side by side with nothing
            between them, which is how a customisable toolbar usually looks broken. A separator
            survives only if a button follows it and one precedes it. */}
        {QAT
          .filter((id) => id === null || !QAT_TICK[id] || chrome.cfg.qat[QAT_TICK[id]])
          .filter((id, i, a) => id !== null
                                || (i > 0 && i < a.length - 1 && a[i - 1] !== null))
          .map((id, i) => (id === null
            ? <span key={`s${i}`} style={{ width: 1, height: 15, background: ui.edge,
                                           margin: "0 3px" }} />
            : <Btn key={id} b={BTN_BY_ID[id] || { id, label: id }} chrome={chrome} size={16} />))}
        <span style={{ position: "relative" }}>
          <span data-testid="acad-qat-menu" title="Customize Quick Access Toolbar"
            onClick={() => setQatMenu(!qatMenu)}
            style={{ padding: "0 5px", cursor: "pointer", color: ui.soft, fontSize: 9 }}>▾</span>
          {qatMenu ? (
            <div style={{ position: "absolute", top: "100%", left: 0 }}
              onMouseLeave={() => setQatMenu(false)}>
              <MenuItems items={QAT_MENU} chrome={chrome} onRun={() => setQatMenu(false)} />
            </div>) : null}
        </span>
      </div>

      <span style={{ flex: 1, textAlign: "center", color: ui.soft, fontSize: 11.5 }}>
        {/* ⚠ AutoCAD marks an unsaved drawing with an asterisk in the title, and with the
            studio's own header gone this is the ONLY place the drawing's name appears. */}
        {/* ⚠⚠ **NOT "Autodesk AutoCAD" — THIS IS NOT AUTODESK'S PROGRAM.** The chrome is
            modelled on AutoCAD's, deliberately and closely, because a drafter's hands already
            know it. Wearing its NAME is a different thing entirely: it is a trademark, and a
            title bar is exactly where a user decides what program they are running. */}
        {/* ⚠ Strip an extension the name already carries. A drawing imported from
            `PLAN.dwg` is stored under that whole name, so appending `.dwg` unconditionally
            put "11.SD-FA-401.dwg.dwg" in the title bar. */}
        {`pdcad  ·  ${title.replace(/\.(dwg|dxf)$/i, "")}.dwg${dirty ? "*" : ""}`}
      </span>

      {user ? (
        <span data-testid="acad-user" onClick={onUser} title="Sign out"
              style={{ display: "flex", alignItems: "center", gap: 5, padding: "0 10px",
                       cursor: "pointer", color: ui.soft, fontSize: 11.5 }}>
          <span style={{ width: 16, height: 16, borderRadius: 8, background: "#4a6d9c",
                         color: "#fff", display: "grid", placeItems: "center",
                         font: "700 9px system-ui" }}>
            {user.slice(0, 1).toUpperCase()}
          </span>
          {user}
        </span>
      ) : null}

      <span style={{ position: "relative" }}>
        <span data-testid="acad-workspace" onClick={() => setWsMenu(!wsMenu)}
          style={{ display: "flex", alignItems: "center", gap: 5, padding: "0 8px",
                   cursor: "pointer", color: ui.soft, fontSize: 11.5 }}>
          {/* ⚠ The NAME OF THE CURRENT WORKSPACE, not a fixed string. It was hard-coded while
              there was only one workspace, and stayed hard-coded when there were three — so the
              chip that exists to tell you which one you are in went on saying "Drafting &
              Annotation" while the ribbon in front of it showed Solid, Surface and Mesh. */}
          <Icon id="man.cui" size={14} />{WS_NAME[ws] || WS_NAME["ws.drafting"]}
          <span style={{ fontSize: 8 }}>▾</span>
        </span>
        {wsMenu ? (
          <div style={{ position: "absolute", top: "100%", right: 0 }}
            onMouseLeave={() => setWsMenu(false)}>
            <MenuItems items={WORKSPACES} chrome={chrome} onRun={() => setWsMenu(false)} />
          </div>) : null}
      </span>
    </div>);
}

// ── the Application menu ─────────────────────────────────────────────────────────────────────

export function AcadAppMenu({ chrome }: { chrome: AcadChrome }) {
  const { ui } = chrome;
  if (!chrome.appMenu) return null;
  return (
    <div onClick={() => chrome.setAppMenu(false)}
      style={{ position: "fixed", inset: 0, zIndex: 958, background: "rgba(0,0,0,0.10)" }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="acad-appmenu"
        style={{ position: "absolute", left: 4, top: 28, width: 320, background: ui.menuBg,
                 border: `1px solid ${ui.menuLine}`, boxShadow: "3px 4px 12px rgba(0,0,0,0.45)",
                 font: `${ui.size}px ${ui.font}`, color: ui.fg }}>
        <MenuItems items={APP_MENU} chrome={chrome} onRun={() => chrome.setAppMenu(false)} />
        {/* ⚠⚠ AutoCAD's Application menu ends in a bar with exactly two buttons, and one of them
            is OPTIONS. That matters more here than fidelity: this shell replaces the studio's
            header, so the interface switch that used to be a button is gone and Ctrl+K is the
            only route left — fine once you know it, a trap until you do. This is the button a
            person actually finds, and it is visible by default, which Tools ▸ Options is not
            while the menu bar is off. */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, padding: 8,
                      borderTop: `1px solid ${ui.sep}`, background: ui.ribbon }}>
          <button data-testid="acad-appmenu-options"
            onClick={() => { chrome.setAppMenu(false); chrome.fire("app.options"); }}
            style={{ padding: "4px 14px", font: `${ui.size}px ${ui.font}`, cursor: "pointer",
                     background: ui.panel, color: ui.fg, border: `1px solid ${ui.edge}`,
                     borderRadius: 2 }}>Options</button>
          <button data-testid="acad-appmenu-exit"
            onClick={() => { chrome.setAppMenu(false); chrome.fire("file.exit"); }}
            style={{ padding: "4px 14px", font: `${ui.size}px ${ui.font}`, cursor: "pointer",
                     background: ui.panel, color: ui.fg, border: `1px solid ${ui.edge}`,
                     borderRadius: 2 }}>Exit AutoCAD</button>
        </div>
      </div>
    </div>);
}

// ── the drawing (file) tabs ──────────────────────────────────────────────────────────────────

export function AcadFileTabs({ chrome, title, dirty }: {
  chrome: AcadChrome; title: string; dirty?: boolean;
}) {
  const { ui, cfg } = chrome;
  if (!cfg.filetabs || cfg.clean) return null;
  const tab = (id: string, label: string, on: boolean) => {
    const h = norm(chrome.all[id]);
    const canGo = live(h);
    return (
      <div key={id} data-testid={`acad-filetab-${id}`} data-acad-off={canGo || on ? undefined : "1"}
        title={on ? "" : tip(BTN_BY_ID[id], h, label)}
        onClick={() => { if (!on) chrome.fire(id); }}
        style={{ display: "flex", alignItems: "center", gap: 6, padding: "0 14px", height: 22,
                 cursor: on ? "default" : canGo ? "pointer" : "default",
                 background: on ? ui.ribbon : "transparent",
                 color: on ? ui.fg : canGo ? ui.soft : ui.dim,
                 borderTop: `2px solid ${on ? ui.accent : "transparent"}`,
                 borderRight: `1px solid ${ui.line}` }}>{label}</div>);
  };
  return (
    <div data-testid="acad-filetabs"
      style={{ height: 22, flex: "0 0 auto", display: "flex", alignItems: "stretch",
               background: ui.tabs, borderBottom: `1px solid ${ui.line}`,
               font: `${ui.size}px ${ui.font}`, userSelect: "none" }}>
      {tab("file.open", "Start", false)}
      {tab("dwg.current", `${title}${dirty ? "*" : ""}`, true)}
      {tab("file.new", "+", false)}
    </div>);
}

// ── the layout tabs ──────────────────────────────────────────────────────────────────────────

export function AcadLayoutTabs({ chrome, layouts }: {
  chrome: AcadChrome;
  /** The drawing's composed pages — AutoCAD's layout tabs are its layouts, not a fixed pair. */
  layouts?: { id: number; name: string }[];
}) {
  const { ui, cfg } = chrome;
  if (!cfg.layouttabs) return null;
  const tabs = [...LAYOUT_TABS,
                ...(layouts || []).map((p, i) => ({ id: `layout.page.${p.id}`,
                                                    label: p.name || `Layout${i + 1}` }))];
  return (
    <div data-testid="acad-layouttabs"
      style={{ height: 21, flex: "0 0 auto", display: "flex", alignItems: "stretch",
               background: ui.tabs, borderTop: `1px solid ${ui.line}`,
               font: `${ui.size}px ${ui.font}`, userSelect: "none" }}>
      {tabs.map((l) => {
        const h = norm(chrome.all[l.id]);
        const on = !!h.on;
        const can = live(h);
        return (
          <div key={l.id} data-testid={`acad-layout-${l.id}`}
            data-acad-off={can ? undefined : "1"}
            title={tip(BTN_BY_ID[l.id] || { id: l.id, label: l.label }, h, l.label)}
            // ⚠ The label is passed as the echoed name. A layout's id is generated from its page
            // (`layout.page.1263`), so it is not in `BTN_BY_ID` and `fire` would print the id —
            // AutoCAD echoes the layout's NAME, and so should the transcript.
            onClick={() => chrome.fire(l.id, l.label)}
            style={{ display: "flex", alignItems: "center", padding: "0 14px",
                     cursor: can && !on ? "pointer" : "default",
                     background: on ? ui.ribbon : "transparent",
                     color: on ? ui.fg : can ? ui.soft : ui.dim,
                     borderTop: `2px solid ${on ? ui.accent : "transparent"}`,
                     borderRight: `1px solid ${ui.line}` }}>{l.label}</div>);
      })}
      {/* ⚠⚠ **THE "+" IS LIVE WHEN THE HOST CAN MAKE A LAYOUT, AND GREY WHEN IT CANNOT.** It
          used to be grey unconditionally with a note pointing at the design app's sheet
          composer — true there, and false in a CAD file, where a layout is part of the document
          and `LAYOUT ▸ New` is how you get one. `layout.new` decides: if the host wired it, this
          works; if not, the old note is still the right answer. */}
      {(() => {
        const h = norm(chrome.all["layout.new"]);
        const can = live(h);
        return (
          <div data-testid="acad-layout-add" data-acad-off={can ? undefined : "1"}
            onClick={() => { if (can) chrome.fire("layout.new", "_LAYOUT"); }}
            title={can ? "New Layout\nLAYOUT"
                       : "⚠ sheets are composed on the drawing's data sheet ▸ Sheets"}
            style={{ display: "flex", alignItems: "center", padding: "0 10px",
                     cursor: can ? "pointer" : "default", color: can ? ui.soft : ui.dim }}>+</div>);
      })()}
    </div>);
}

// ── the classic docked toolbars ──────────────────────────────────────────────────────────────

/** ⚠ Every toolbar the Toolbars list offers RENDERS. Listing one that draws nothing would be
 *  the SketchUp lane's exact mistake — five checkboxes in the mode's own settings dialog that
 *  ticked and did nothing, shipped inside the feature built to prevent that. */
export function AcadToolbars({ chrome, active }: { chrome: AcadChrome; active: string | null }) {
  const { ui, cfg } = chrome;
  const shown = TOOLBARS.filter((t) => cfg.toolbars[t.id]);
  if (cfg.clean || !shown.length) return null;
  return (
    <div data-testid="acad-toolbars"
      style={{ flex: "0 0 auto", display: "flex", background: ui.ribbon,
               borderRight: `1px solid ${ui.line}` }}>
      {shown.map((t) => (
        <div key={t.id} data-testid={`acad-toolbar-${t.id}`}
          style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1,
                   padding: "3px 2px", borderRight: `1px solid ${ui.line}`, overflowY: "auto" }}>
          {t.items.map((id, i) => (id === null
            ? <span key={`s${i}`} style={{ height: 1, width: 18, background: ui.edge,
                                           margin: "3px 0" }} />
            : <Btn key={id} b={BTN_BY_ID[id] || { id, label: id }} chrome={chrome}
                   active={active === id} size={18} />))}
        </div>))}
    </div>);
}

// ── the docked palettes ──────────────────────────────────────────────────────────────────────

export function AcadPalettes({ chrome, body }: {
  chrome: AcadChrome; body: (id: string) => React.ReactNode;
}) {
  const { ui, cfg } = chrome;
  const open = PALETTES.filter((p) => p.panel && norm(chrome.all[p.id]).on);
  if (cfg.clean || !open.length) return null;
  return (
    <div data-testid="acad-palettes"
      style={{ width: 268, flex: "0 0 auto", display: "flex", flexDirection: "column",
               background: ui.panel, borderLeft: `1px solid ${ui.line}`,
               font: `${ui.size}px ${ui.font}`, color: ui.fg, overflow: "hidden" }}>
      {open.map((p) => (
        <div key={p.id} data-testid={`acad-palette-${p.id}`}
          style={{ display: "flex", flexDirection: "column", flex: "1 1 0", minHeight: 90,
                   borderBottom: `1px solid ${ui.line}` }}>
          {/* AutoCAD's docked palettes carry their name on a bar with a close X */}
          <div style={{ height: 22, flex: "0 0 auto", display: "flex", alignItems: "center",
                        gap: 6, padding: "0 6px", background: ui.tabs, fontWeight: 600,
                        borderBottom: `1px solid ${ui.line}` }}>
            <Icon id={p.id} size={14} />
            <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden",
                           textOverflow: "ellipsis" }}>{p.label}</span>
            <span data-testid={`acad-palette-close-${p.id}`} title="Close"
              onClick={() => chrome.fire(p.id)}
              style={{ cursor: "pointer", color: ui.soft, padding: "0 2px" }}>✕</span>
          </div>
          <div style={{ flex: 1, overflowY: "auto", background: ui.sunk }}>
            {body(p.id) ?? <div style={{ padding: 8, color: ui.dim }}>Not built yet.</div>}
          </div>
        </div>))}
    </div>);
}

// ── the furniture that floats over the drawing ───────────────────────────────────────────────

/** The UCS icon, bottom-LEFT of the drawing, which is where AutoCAD puts it.
 *
 * ⚠⚠ **RENDERED FROM THE `rail` SLOT, NOT THE PALETTE COLUMN, AND THAT IS THE WHOLE REASON IT
 * IS A SEPARATE COMPONENT.** It shipped in the `side` slot first and came out at the drawing's
 * RIGHT edge — 1 458 px across a 1 532 px canvas — because `side` is a flex sibling on the far
 * side of the drawing and a negative offset from there lands nowhere near the origin. The rail
 * is the sibling on the LEFT, so a zero-width strip there with a positive offset floats over
 * the left edge, with no ref to the canvas and no measurement.
 *
 * ⚠⚠ **THE Y ARROW POINTS UP — AND THE COMMENT THAT USED TO SIT HERE SAYING IT MUST POINT DOWN
 * WAS WRONG ON ITS OWN PREMISE.** It said "this canvas's world Y increases DOWN the screen, the
 * coordinate readout says so" and drew the icon accordingly. `Canvas2D.toScreen` is
 * `sy = h/2 - (my - vy) * scale` — world Y increases UP. Measured a second way on 2026-09-04:
 * setting a UCS with the second pick DOWN and RIGHT of the origin on screen reports −35.1°, and
 * a negative angle from a downward pick is only possible in a Y-up world. So for as long as
 * this icon has existed it has been the one thing on screen lying about the drawing's own axes
 * — exactly what that comment was trying to prevent.
 *
 * ⚠ Which makes the ROTATION sign the same question. A world angle is counter-clockwise from
 * +X in a Y-UP frame; SVG's `rotate()` is clockwise-positive in its Y-DOWN frame. So the angle
 * goes in NEGATED. Get it wrong and the icon mirrors about the X axis — and still looks exactly
 * like an axis marker, which is why the two picks in the shot are the test and not the picture.
 */
export function AcadUcsIcon(
  { chrome, ucs }: { chrome: AcadChrome; ucs?: { x: number; y: number; rot: number } | null },
) {
  const { ui, cfg } = chrome;
  if (cfg.clean || !cfg.ucsicon) return null;
  // ⚠⚠ **THE ICON TURNS WITH THE UCS, OR IT IS DECORATION.** AutoCAD's turns, and it is the only
  // thing on screen that says which way the axis a drafter is about to type `@100,0` along
  // actually runs. See the docstring for the sign.
  // ⚠ A PROP, not read off `chrome`: `AcadChrome` is the shell's own furniture — toolbars,
  // menus, the transcript — and the studio's state does not belong in it. `shell.tsx` has both
  // and is the one place that can hand this across.
  const u = ucs;
  const rotDeg = ((u?.rot ?? 0) * 180) / Math.PI;
  // ⚠ The square at the corner is AutoCAD's own "you are at the WORLD origin" mark. Leaving it
  // on for a moved UCS would say the opposite of the truth in the one glyph reserved for it.
  const atWorld = !u || (Math.abs(u.x) < 1e-9 && Math.abs(u.y) < 1e-9 && Math.abs(u.rot) < 1e-12);
  return (
    <div style={{ width: 0, flex: "0 0 auto", position: "relative", overflow: "visible",
                  pointerEvents: "none", zIndex: 700 }}>
      <div data-testid="acad-ucsicon"
        title={(atWorld ? "UCS icon — World"
                        : `UCS icon — origin ${(u?.x ?? 0).toFixed(1)}, ${(u?.y ?? 0).toFixed(1)}`
                          + (Math.abs(rotDeg) > 1e-9 ? ` at ${rotDeg.toFixed(1)}°` : ""))
               + "\n⚠ this drawing's Y axis runs DOWN the screen, and the icon "
               + "shows it that way on purpose"}
        // ⚠⚠ **NO POINTER EVENTS, AND THIS COST A REAL BUTTON.** The wrapper switches them off
        // and this div used to switch them back ON, only so the `title` tooltip would appear on
        // hover. The icon sits at the drawing's bottom-left — which is exactly where the studio
        // floats its note editor — so 62 × 62 pixels of a decorative axis marker were sitting on
        // top of that card's Place button and eating every click on it. Found 2026-08-31 when
        // MTEXT's own test could not press Save. AutoCAD's UCS icon IS selectable; ours is
        // explicitly inert, so it has no business taking a click. The fact it was advertising
        // now lives on the coordinate readout, which is a real control and is about coordinates.
        // ⚠⚠ **THE ORIGIN SITS AT THE CENTRE OF THE BOX, WHICH IS WHY THE OFFSETS ARE
        // NEGATIVE.** A 62-square with the corner in it clipped the X arm the moment the icon
        // turned — the arrow simply ended in mid-air, which reads as a rendering bug rather than
        // a rotated axis. Centring it means no rotation can leave the viewBox; the offsets then
        // put the corner back at the same screen pixel it used to be at (20 from the left edge,
        // 18 up from the bottom), so nothing moved for a drafter in the world UCS.
        style={{ position: "absolute", left: -30, bottom: -32, pointerEvents: "none",
                 opacity: 0.85 }}>
        <svg width="100" height="100" viewBox="0 0 100 100" aria-hidden>
          {/* ⚠ The corner is at (50, 50) — the centre, so the arms swing inside the box. */}
          <g fill="none" strokeLinecap="round" strokeLinejoin="round"
             transform={`rotate(${(-rotDeg).toFixed(3)} 50 50)`}>
            <path d="M50 50h38" stroke="#D9584F" strokeWidth="2.4" />
            <path d="M82 45.5L88 50l-6 4.5" stroke="#D9584F" strokeWidth="2.4" />
            <path d="M50 50V12" stroke="#5FAE63" strokeWidth="2.4" />
            <path d="M45.5 18L50 12l4.5 6" stroke="#5FAE63" strokeWidth="2.4" />
            {atWorld
              ? <rect x="50" y="41" width="9" height="9" stroke={ui.soft} strokeWidth="1.2" />
              : null}
            {/* ⚠ The letters counter-rotate, so they stay readable at a rotated UCS the way
                AutoCAD's do — an upside-down X is not a label. */}
            <text x="91" y="54" fontSize="11" fill="#D9584F" fontFamily={ui.mono}
                  transform={`rotate(${rotDeg.toFixed(3)} 91 50)`}>X</text>
            <text x="44" y="9" fontSize="11" fill="#5FAE63" fontFamily={ui.mono}
                  transform={`rotate(${rotDeg.toFixed(3)} 50 12)`}>Y</text>
          </g>
        </svg>
      </div>
    </div>);
}

/** The ViewCube and the navigation bar, floating over the drawing's RIGHT edge — which is where
 *  AutoCAD floats both, and which the palette column is the correct sibling for. */
export function AcadCanvasFurniture({ chrome }: { chrome: AcadChrome }) {
  const { ui, cfg } = chrome;
  if (cfg.clean) return null;
  // ⚠ Read off the handler table rather than passed in — `view.3d.top` reports `on` when the
  // camera is on the plan, so the cube cannot disagree with the menu about where you are.
  const cam3d = !norm(chrome.all["view.3d.top"]).on;
  const nav: string[] = ["view.zoom.extents", "view.pan", "view.orbit", "view.zoom.window"];
  // ⚠⚠⚠ **WHICH TOGGLES ARE LIT, AS TEXT — THE APP'S OWN TRUTH, PUBLISHED ONCE.** A command
  // whose whole effect is to turn something ON changes no transcript, no count and no dialog:
  // VSCURRENT picks a visual style, ANALYSISZEBRA switches the shading, PRESSPULL arms a tool.
  // `command_survey.py` reported all of them SILENT, and they were the last of a bucket that had
  // already shrunk from 20 to 12 for the same reason twice over — the instrument could not see
  // an answer the app was giving.
  // ⚠ Read off `chrome.all`, which is where every handler already declares `on`. A second list
  // of "toggles worth publishing" would be a second source of truth about the UI, which is the
  // fault this project keeps finding in itself.
  // ⚠ Rendered but not VISIBLE: it is a readout for instruments, and a drafter has the lit
  // buttons themselves.
  const lit = Object.keys(chrome.all).filter((k) => norm(chrome.all[k]).on).sort().join(" ");
  return (
    <div style={{ width: 0, flex: "0 0 auto", position: "relative", overflow: "visible",
                  pointerEvents: "none", zIndex: 700 }}>
      <span data-testid="acad-lit" style={{ position: "absolute", width: 1, height: 1,
                                            overflow: "hidden", clip: "rect(0 0 0 0)",
                                            whiteSpace: "nowrap" }}>{lit}</span>
      {cfg.viewcube ? (
        // ⚠⚠ **IT DOES SOMETHING NOW.** From the day this shell was built until 2026-08-31 the
        // cube was drawn at 34% and `disabled` — deliberately, because a cube you can click
        // that does nothing is the exact failure this mode exists to prevent. The 3D viewport
        // is here, so it is a control: click to leave Top view, click again to come back.
        // ⚠ Its FACES are not individually pickable and the tooltip says so. A cube whose
        // corners looked clickable and were not would be the old lie in a smaller box.
        <div data-testid="acad-viewcube"
          onClick={() => chrome.fire(cam3d ? "view.3d.top" : "view.orbit")}
          title={cam3d
            ? "ViewCube — click to return to Top view (the drawing)\n"
              + "⚠ the individual faces and corners are not pickable; "
              + "View ▸ 3D Views names each one"
            : "ViewCube — click for an isometric view of the model\n"
              + "⚠ Top view IS the drawing canvas, which is what you are looking at"}
          style={{ position: "absolute", left: -86, top: 8, width: 76, height: 76,
                   pointerEvents: "auto", opacity: cam3d ? 1 : 0.72, display: "grid",
                   placeItems: "center", cursor: "pointer" }}>
          <Icon id="view.viewcube" size={62} />
          <span style={{ position: "absolute", bottom: -3, left: 0, right: 0, textAlign: "center",
                         fontSize: 9, letterSpacing: 0.4, color: ui.soft,
                         textTransform: "uppercase" }}>
            {cam3d ? "3D" : "TOP"}</span>
        </div>) : null}
      {cfg.navbar ? (
        <div data-testid="acad-navbar"
          style={{ position: "absolute", left: -32, top: 96, width: 28, pointerEvents: "auto",
                   background: ui.panel, border: `1px solid ${ui.edge}`, borderRadius: 3,
                   display: "flex", flexDirection: "column", alignItems: "center",
                   padding: "2px 0" }}>
          {nav.map((id) => (
            <Btn key={id} b={BTN_BY_ID[id] || { id, label: id }} chrome={chrome} size={16} />))}
        </div>) : null}
    </div>);
}

// ── the command line ─────────────────────────────────────────────────────────────────────────

/** AutoCAD's command line, and it is a real one.
 *
 * ⚠⚠ **IT DOES NOT TAKE FOCUS UNTIL YOU CLICK IT, AND THAT IS THE OPPOSITE OF AUTOCAD.** In
 * AutoCAD every keystroke goes here. Here, the DRAWING owns the keyboard: `PlanStudio` reads
 * typed lengths and angles off the canvas, and its key handler bails the moment the event target
 * is an `<input>`. Autofocusing this box would silently break typed input across the whole
 * studio — the exact trap the SketchUp lane avoided by making its Measurements box not an input
 * at all. So: click to type a command; while it is not focused it MIRRORS the studio's own
 * command buffer, so letters typed at the drawing still appear here, which is what a drafter is
 * actually watching for.
 *
 * ⚠ And after a command that arms a tool, focus is handed straight back to the drawing — see
 * `AcadHandler.arms`. Otherwise `L` ⏎ `3000` ⏎ would put the 3000 in this box.
 */
export function AcadCommandLine({ chrome, typed, prompt }: {
  chrome: AcadChrome;
  /** The studio's own command buffer, shown while this box does not have focus. */
  typed?: string;
  /** What the running command is asking for, if anything. */
  prompt?: string;
}) {
  const { ui, cfg } = chrome;
  const [text, setText] = useState("");
  const [pick, setPick] = useState(0);
  const [focus, setFocus] = useState(false);
  const inp = useRef<HTMLInputElement | null>(null);
  const scroll = useRef<HTMLDivElement | null>(null);
  /** AutoCAD's F2 text window — the whole transcript, not the last three lines.
   *
   * ⚠⚠ **THE COMMAND LINE SHOWS THREE ROWS AND A DRAFTER READS BACK MUCH FURTHER THAN THAT.**
   * "how many did that TRIM find", "what did the last PURGE remove", "what was the error" — all
   * of it scrolls away in seconds. F2 is where thirty years of AutoCAD users go to look, and
   * pressing it here did nothing at all.
   * ⚠ Handled in THIS component rather than in the studio's key handler: the transcript lives
   * here, and a second copy of the history in another file is a second thing to keep in step. */
  const [textWin, setTextWin] = useState(false);
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key !== "F2") return;
      // ⚠ In AutoCAD F2 fires whatever has focus, including a text box — the same rule the
      // studio's own handler follows for F3 and F8.
      e.preventDefault();
      setTextWin((v) => !v);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, []);

  chrome.releaseKeyboard.current = () => { inp.current?.blur(); };
  // ⚠ APPENDS rather than replaces: the drafter may already have typed `RE` before the guard in
  // `shell.tsx` let the third letter through, and a box that resets to one character on every
  // keystroke would be unusable. Focusing is what makes every LATER keystroke arrive here by
  // itself — this callback fires once, for the first letter of a command.
  chrome.takeKeyboard.current = (ch: string) => {
    setText((t) => t + ch);
    setPick(0);
    inp.current?.focus();
  };

  useEffect(() => {
    if (scroll.current) scroll.current.scrollTop = scroll.current.scrollHeight;
  }, [chrome.hist.length]);

  if (!cfg.cmdline) return null;

  const q = text.trim().toUpperCase();
  const matches = q ? ALL_COMMANDS.filter((c) => c.startsWith(q)).slice(0, 8) : [];

  const submit = (raw: string) => {
    const t = raw.trim();
    setText("");
    setPick(0);
    if (!t) {
      // ⚠ Enter on an empty command line REPEATS the last command. Thirty years of AutoCAD
      // muscle memory runs on this and its absence is felt within a minute.
      if (chrome.lastId) chrome.fire(chrome.lastId);
      else chrome.say("Command:");
      return;
    }
    // ⚠⚠ **`(` IS LISP, BEFORE ANY NAME LOOKUP.** `(command "_.LINE" …)` is not a command name
    // and never was; AutoCAD evaluates it at the prompt and prints the answer. See `evalLisp`.
    if (t.startsWith("(")) { chrome.say(t); chrome.evalLisp.current?.(t); return; }
    const { id, cmd } = resolveCommand(t);
    if (!id) {
      // ⚠ A `.lsp` file may have DEFINED this name — `(defun c:BEAM …)` — and APPLOAD/LOAD are
      // answered here too. Only when the name is nobody else's.
      if (chrome.lispCmd.current?.(cmd)) return;
      // AutoCAD's own wording, two spaces and all.
      chrome.say(`Unknown command "${cmd}".  Press F1 for help.`);
      return;
    }
    chrome.fire(id, `_${cmd}`);
  };

  const rows = Math.max(1, Math.min(14, cfg.cmdRows));
  return (
    <>
    {/* ⚠ AutoCAD's F2 window floats OVER the drawing and is dismissed by F2 again or by ESC.
        Deliberately plain: the transcript, monospaced, scrolled to the bottom. */}
    {textWin ? (
      <div data-testid="acad-textwindow"
        style={{ position: "fixed", left: "12%", top: "10%", width: "62%", height: "60%",
                 zIndex: 990, display: "flex", flexDirection: "column",
                 background: ui.cmdBg, color: ui.cmdFg, border: `1px solid ${ui.line}`,
                 boxShadow: "0 8px 32px rgba(0,0,0,.55)", font: `11.5px ${ui.mono}` }}>
        <div style={{ display: "flex", alignItems: "center", height: 22, padding: "0 8px",
                      background: ui.title, color: ui.fg, font: `11px ${ui.font}`,
                      borderBottom: `1px solid ${ui.line}` }}>
          <span style={{ flex: 1 }}>pdcad Text Window</span>
          <span data-testid="acad-textwindow-close" onClick={() => setTextWin(false)}
            style={{ cursor: "pointer", padding: "0 6px" }}>✕</span>
        </div>
        <div ref={(el) => { if (el) el.scrollTop = el.scrollHeight; }}
          style={{ flex: 1, overflowY: "auto", padding: "4px 8px", whiteSpace: "pre-wrap" }}>
          {chrome.hist.map((l, i) => <div key={i} style={{ lineHeight: "15px" }}>{l}</div>)}
        </div>
      </div>) : null}
    <div data-testid="acad-cmdline"
      style={{ flex: "0 0 auto", background: ui.cmdBg, borderTop: `1px solid ${ui.line}`,
               font: `11.5px ${ui.mono}`, color: ui.cmdFg }}>
      <div style={{ display: "flex", alignItems: "center", height: 13, padding: "0 6px",
                    background: ui.tabs, color: ui.soft, fontSize: 9.5 }}>
        <span style={{ flex: 1 }} />
        <span data-testid="acad-cmdrows" style={{ cursor: "pointer", padding: "0 4px" }}
          title="Taller command history"
          onClick={() => chrome.set({ cmdRows: rows >= 12 ? 3 : rows + 4 })}>
          {rows >= 12 ? "▼" : "▲"}</span>
      </div>
      <div ref={scroll} data-testid="acad-cmdhistory"
        style={{ height: rows * 15, overflowY: "auto", padding: "2px 8px",
                 whiteSpace: "pre-wrap" }}>
        {/* ⚠⚠ **THE TAIL, NOT ALL 400 LINES.** Every prompt this app prints re-rendered the
            WHOLE transcript — 400 `div`s of layout for a box three lines tall — and a typed
            command prints three or four of them. The scrollback still exists: F2 opens the text
            window, which renders `chrome.hist` in full, and that is AutoCAD's own answer for
            reading back further. ⚠ Four screenfuls kept, so dragging the box taller still has
            something in it. */}
        {chrome.hist.slice(-Math.max(40, rows * 4)).map((l, i) => (
          <div key={i} style={{ lineHeight: "15px",
                                // a grey line in the transcript is a command that did not run
                                color: l.includes("  ·  ") ? ui.dim
                                     : l.startsWith("Unknown") ? "#D9584F" : ui.cmdFg }}>{l}</div>))}
      </div>
      <div style={{ position: "relative", display: "flex", alignItems: "center", gap: 6,
                    padding: "0 8px", height: 22, borderTop: `1px solid ${ui.line}` }}>
        <span data-testid="acad-prompt" style={{ color: ui.soft, whiteSpace: "nowrap" }}>
          {prompt || "Command:"}</span>
        <input ref={inp} data-testid="acad-cmdinput" value={text}
          onFocus={() => setFocus(true)} onBlur={() => setFocus(false)}
          placeholder={focus ? "" : (typed || "type a command")}
          onChange={(e) => { setText(e.target.value); setPick(0); }}
          onKeyDown={(e) => {
            // ⚠⚠ **A GUARD FOR "A COMMAND IS RUNNING" DOES NOT BELONG HERE, AND ONE WAS TRIED.**
            // `if (!text.trim() && prompt) return;` looks right and is wrong twice over: `prompt`
            // is the LITERAL string "Command:" when nothing is running, so it suppressed
            // repeat-last-command entirely — ENTER stopped repeating and SPACE started inserting
            // a space, both caught by `qa/ux_survey.py` within one run. And it was unnecessary:
            // during a command the focus is the dynamic-input box, not this one, so Enter never
            // arrives here anyway. `<use first point as displacement>` is answered in
            // `Studio.finishCmdPoint`, which is where the keystroke actually lands.
            if (e.key === "Enter") {
              e.preventDefault();
              // ⚠⚠ **AN EXACT COMMAND NAME WINS OVER THE HIGHLIGHTED COMPLETION.** Typing a
              // whole name and pressing Enter must run THAT command — `DIST` is a prefix of
              // `DISTANTLIGHT`, `TEXT` of `TEXTEDIT`, `ARC` of `ARCHIVE`. The completion list
              // is sorted so the exact match is usually first, which makes this latent rather
              // than harmless: one alias sorting oddly and Enter runs the wrong command.
              const exact = matches.find((c) => c === q);
              submit(exact ?? (matches[pick] || text));
            }
            // ⚠⚠ **SPACE IS ENTER AT AutoCAD'S COMMAND LINE, AND IT IS THE KEY A DRAFTER'S LEFT
            // THUMB PRESSES ALL DAY.** ENTER is the exception; SPACE ends every command, and on
            // an empty line it repeats the last one. Without this, `LINE ` sat in the box as
            // literal text and every command in the app needed a deliberate reach to finish —
            // invisible to the render gate and to the e2e suite, because the drawing that comes
            // out is identical when you press ENTER instead. Measured by `qa/ux_survey.py`.
            // ⚠ Safe HERE and nowhere else: this input takes COMMAND NAMES, and no command name
            // contains a space. Text strings, block names and file names are answered in the
            // dynamic-input box, where SPACE must stay a space — see `Studio.tsx`'s `dynin`.
            else if (e.key === " ") {
              e.preventDefault();
              const exact = matches.find((c) => c === q);
              submit(exact ?? (matches[pick] || text));
            }
            else if (e.key === "Tab" && matches.length) {
              e.preventDefault(); setText(matches[pick]);
            } else if (e.key === "ArrowDown" && matches.length) {
              e.preventDefault(); setPick((p) => (p + 1) % matches.length);
            } else if (e.key === "ArrowUp" && matches.length) {
              e.preventDefault(); setPick((p) => (p - 1 + matches.length) % matches.length);
            } else if (e.key === "Escape") {
              e.preventDefault();
              if (text) { setText(""); }
              else { chrome.fire("edit.cancel", "*Cancel*"); inp.current?.blur(); }
            }
          }}
          style={{ flex: 1, background: "transparent", border: "none", outline: "none",
                   font: `11.5px ${ui.mono}`, color: ui.cmdFg }} />
        {/* AutoCAD's AutoComplete — every match greys exactly as its button does */}
        {matches.length ? (
          <div data-testid="acad-autocomplete"
            style={{ position: "absolute", bottom: "100%", left: 8, minWidth: 240, zIndex: 955,
                     background: ui.menuBg, border: `1px solid ${ui.menuLine}`,
                     boxShadow: "1px -2px 8px rgba(0,0,0,0.4)" }}>
            {matches.map((c, i) => {
              const { id } = resolveCommand(c);
              const h = norm(chrome.all[id || ""]);
              const on = !!id && live(h);
              return (
                <div key={c} data-acad-off={on ? undefined : "1"}
                  onMouseDown={(e) => { e.preventDefault(); submit(c); }}
                  onMouseEnter={() => setPick(i)}
                  style={{ display: "flex", gap: 8, alignItems: "center", padding: "2px 8px",
                           cursor: "pointer", background: i === pick ? ui.hover : "transparent",
                           color: on ? ui.fg : ui.dim }}>
                  <Icon id={id || ""} size={14} />
                  <span style={{ flex: 1 }}>{c}</span>
                  <span style={{ fontSize: 10, color: ui.dim }}>
                    {on ? "" : (h.note || "not built yet")}</span>
                </div>);
            })}
          </div>) : null}
      </div>
    </div>
    </>);
}

// ── the status bar ───────────────────────────────────────────────────────────────────────────

export function AcadStatusBar({ chrome, coords, snap, drops }: {
  chrome: AcadChrome;
  coords: string;
  snap?: string;
  /** The drop-ups: `osnap` is the thirteen running snaps, the rest are one list each. */
  drops: Record<string, AcadMenuItem[]>;
}) {
  const { ui, cfg } = chrome;
  const [open, setOpen] = useState<string | null>(null);
  const [custom, setCustom] = useState(false);

  const item = (it: typeof STATUS_ITEMS[number]) => {
    const h = norm(chrome.all[it.id]);
    const on = live(h);
    const lit = !!h.on;
    return (
      <span key={it.id} style={{ position: "relative", display: "flex", alignItems: "center" }}>
        {/* ⚠ `data-acad-on` as well as `data-acad-off`: "is this button available" and "is the
            thing it controls switched on" are different questions, and a probe that can only ask
            the first has to read a computed background colour to guess at the second. */}
        <span data-testid={`acad-status-${it.id}`} data-acad-off={on ? undefined : "1"}
          data-acad-on={lit ? "1" : undefined}
          title={tip(BTN_BY_ID[it.id], h, it.label)}
          onClick={() => { if (on) chrome.fire(it.id); }}
          style={{ display: "grid", placeItems: "center", width: 22, height: 20,
                   cursor: on ? "pointer" : "default", opacity: on ? 1 : 0.34,
                   background: lit ? ui.press : "transparent", borderRadius: 2 }}>
          <Icon id={it.id} size={15} />
        </span>
        {it.drop && drops[it.drop] ? (
          <>
            <span data-testid={`acad-drop-${it.drop}`}
              onClick={() => setOpen(open === it.drop ? null : it.drop!)}
              style={{ fontSize: 8, cursor: "pointer", color: ui.soft, padding: "0 2px" }}>▴</span>
            {open === it.drop ? (
              <div style={{ position: "absolute", bottom: "100%", left: 0 }}
                onMouseLeave={() => setOpen(null)}>
                <MenuItems items={drops[it.drop!]} chrome={chrome} onRun={() => setOpen(null)} />
              </div>) : null}
          </>) : null}
      </span>);
  };

  return (
    <div data-testid="acad-statusbar"
      style={{ height: 24, flex: "0 0 auto", display: "flex", alignItems: "center", gap: 2,
               padding: "0 6px", background: ui.title, borderTop: `1px solid ${ui.line}`,
               font: `11px ${ui.font}`, color: ui.fg, userSelect: "none" }}>
      {/* ⚠ AutoCAD's coordinate readout is the leftmost thing on the status bar, and it is the
          single most-looked-at number in the application. */}
      <span data-testid="acad-coords"
        title={"Cursor position, in millimetres\n⚠ this drawing's Y axis runs DOWN the screen — "
               + "the UCS icon's green arrow points that way on purpose"}
        style={{ fontFamily: ui.mono, fontSize: 11, color: ui.fg, minWidth: 168 }}>{coords}</span>
      {/* ⚠ Given a testid because it is the only place the app SAYS which snap it caught, and
          "the button is live" is a different claim from "the geometry works". A probe that can
          only read the drop-up can confirm the first and never the second. */}
      {snap ? <span data-testid="acad-snaptag"
        style={{ color: ui.accent, fontSize: 10.5, minWidth: 74 }}>{snap}</span> : null}
      <span style={{ width: 1, height: 14, background: ui.edge, margin: "0 4px" }} />
      {STATUS_ITEMS.map(item)}
      <span style={{ flex: 1 }} />
      <span style={{ position: "relative" }}>
        <span data-testid="acad-status-custom" title="Customization"
          onClick={() => setCustom(!custom)}
          style={{ cursor: "pointer", padding: "0 6px", color: ui.soft }}>☰</span>
        {custom ? (
          <div style={{ position: "absolute", bottom: "100%", right: 0 }}
            onMouseLeave={() => setCustom(false)}>
            <MenuItems chrome={chrome} onRun={() => setCustom(false)}
              items={[
                { id: "app.options", label: "Options..." },
                { id: "ui.cmdline", label: "Command Line", toggle: true },
                { id: "ui.layouttabs", label: "Layout Tabs", toggle: true },
                { id: "ui.filetabs", label: "File Tabs", toggle: true },
                { id: "ui.menubar", label: "Menu Bar", toggle: true },
                { sep: true },
                { id: "ui.cleanscreen", label: "Clean Screen", key: "Ctrl+0", toggle: true },
              ]} />
          </div>) : null}
      </span>
    </div>);
}

/** The OSNAP drop-up, built from the same thirteen modes the toolbar draws. */
export function osnapMenu(): AcadMenuItem[] {
  return [
    ...OSNAP_MODES.map((o) => ({ id: o.id, label: o.label, toggle: true })),
    { sep: true },
    { id: "osnap.settings", label: "Object Snap Settings..." },
  ];
}

// ── the one row this shell does not own ──────────────────────────────────────────────────────

/** Drags the studio's own antd controls into the ribbon's colour scheme while AutoCAD mode is on.
 *
 * ⚠⚠ **THE TOOL-OPTIONS STRIP IS NOT THIS SHELL'S TO RENDER, AND IT CANNOT BE HIDDEN.** It
 * carries wall thickness, draw layer and pen — things the tool is unusable without — and AutoCAD
 * has no wall tool, so there is nowhere native to put a wall thickness. `StudioChrome` offers
 * `optionsStyle` to restyle the CONTAINER, and that is all: the controls inside it are antd
 * components belonging to `DesignStudioPage`, a file this lane does not edit. So a dark ribbon
 * had one row of white chips across it, and it was the only thing on screen that read as a
 * different application. The same was true of the studio panels this shell hosts as palettes.
 *
 * ⚠ **`!important` IS THE CORRECT TOOL HERE, not laziness.** antd v5 emits its styles through
 * CSS-in-JS at a specificity these rules cannot otherwise beat, and several of the targets carry
 * inline styles besides — an author `!important` is the one thing that outranks an inline style.
 *
 * ⚠ **Scoped to a body class that this component owns.** It is added when AutoCAD mode renders
 * and removed when it stops, so leaving the drafting workspace restores every control untouched.
 * Unscoped rules would have followed the user out of the mode.
 *
 * ⚠ Light theme adds nothing — antd's own defaults already match it.
 */
export function AcadThemeBridge({ chrome }: { chrome: AcadChrome }) {
  const { ui, cfg } = chrome;
  const dark = cfg.theme === "dark";
  useEffect(() => {
    if (!dark) return;
    document.body.classList.add("acad-dark");
    return () => { document.body.classList.remove("acad-dark"); };
  }, [dark]);
  if (!dark) return null;
  const css = `
.acad-dark .ant-btn {
  background: ${ui.panel} !important; border-color: ${ui.edge} !important;
  color: ${ui.fg} !important; box-shadow: none !important;
}
.acad-dark .ant-btn:hover:not(:disabled) {
  background: ${ui.hover} !important; border-color: ${ui.hoverLine} !important;
  color: #fff !important;
}
.acad-dark .ant-btn:disabled { color: ${ui.dim} !important; background: ${ui.sunk} !important; }
.acad-dark .ant-btn-primary,
.acad-dark .ant-btn-background-ghost.ant-btn-color-primary {
  background: ${ui.press} !important; border-color: ${ui.hoverLine} !important;
  color: #fff !important;
}
.acad-dark .ant-select-selector, .acad-dark .ant-input, .acad-dark .ant-input-number,
.acad-dark .ant-input-number-input, .acad-dark .ant-input-affix-wrapper,
.acad-dark .ant-input-number-group-addon, .acad-dark .ant-select-selection-search-input {
  background: ${ui.sunk} !important; border-color: ${ui.edge} !important;
  color: ${ui.fg} !important;
}
.acad-dark .ant-select-selection-item, .acad-dark .ant-select-selection-placeholder,
.acad-dark .ant-select-arrow, .acad-dark .ant-input-number-handler-wrap,
.acad-dark .ant-input-number-handler { color: ${ui.fg} !important; background: transparent !important; }
.acad-dark .ant-tag {
  background: ${ui.panel} !important; border-color: ${ui.edge} !important; color: ${ui.fg} !important;
}
/* ⚠ The sheet composer, hosted whole in paper space, brings its own Segmented and Checkbox —
   its "Show the drawing / Frames only" switch sat white-on-white against the dark layout. A
   hosted panel is only as convincing as its controls. */
.acad-dark .ant-segmented {
  background: ${ui.sunk} !important; color: ${ui.soft} !important;
}
.acad-dark .ant-segmented-item-selected, .acad-dark .ant-segmented-thumb {
  background: ${ui.panel} !important;
}
.acad-dark .ant-segmented-item { color: ${ui.soft} !important; }
.acad-dark .ant-segmented-item-selected .ant-segmented-item-label,
.acad-dark .ant-segmented-item:hover { color: ${ui.fg} !important; }
.acad-dark .ant-checkbox-wrapper, .acad-dark .ant-empty-description,
.acad-dark .ant-alert-message, .acad-dark .ant-alert-description { color: ${ui.fg} !important; }
/* the dropdown portals render at body level, so they need the same treatment */
.acad-dark .ant-select-dropdown, .acad-dark .ant-dropdown-menu, .acad-dark .ant-tooltip-inner {
  background: ${ui.menuBg} !important; color: ${ui.fg} !important;
  box-shadow: 2px 3px 8px rgba(0,0,0,0.45) !important;
}
.acad-dark .ant-select-item, .acad-dark .ant-dropdown-menu-item { color: ${ui.fg} !important; }
.acad-dark .ant-select-item-option-active, .acad-dark .ant-select-item-option-selected {
  background: ${ui.hover} !important;
}
.acad-dark input[type=range] { accent-color: ${ui.accent}; }
/* ⚠ The canvas floats a Linear / R / ⌀ / Angle card over itself while the dimension tool is
   active. It is the only way to reach a radius from the classic studio, so it must stay there —
   but this ribbon addresses all four kinds by name, so here it is the same four buttons twice,
   in another application's styling, on top of the drawing. Hidden only while AutoCAD mode is
   on; the class comes off the moment the shell unmounts. */
.acad-dark [data-testid="dim-kind-picker"] { display: none !important; }
`;
  // eslint-disable-next-line react/no-danger -- a stylesheet this component authors in full
  return <style data-testid="acad-theme-bridge" dangerouslySetInnerHTML={{ __html: css }} />;
}

// ── Options ──────────────────────────────────────────────────────────────────────────────────

/** AutoCAD's OPTIONS dialog, cut down to the tabs that mean something here.
 *
 * ⚠ This is also the way OUT of the mode: Profiles carries the interface switch, because the
 * shell has replaced the studio's own header and a person who does not know Ctrl+K would
 * otherwise be stuck in AutoCAD for good. It is deliberately the FIRST thing on that tab. */
export function AcadOptions({ chrome }: { chrome: AcadChrome }) {
  const { ui, cfg } = chrome;
  const [tab, setTab] = useState<"display" | "profiles">("display");
  // ⚠ `chrome.options` IS this dialog's open flag — it lives on the chrome because OPTIONS can
  // be reached from the menu, the ribbon and the command line, so no one caller owns it.
  useEscape(chrome.options, () => chrome.setOptions(false));
  if (!chrome.options) return null;

  const check = (label: string, on: boolean, set: () => void, testid: string) => (
    <label data-testid={`acad-opt-${testid}`}
      style={{ display: "flex", alignItems: "center", gap: 7, padding: "3px 0",
               cursor: "pointer" }}>
      <input type="checkbox" checked={on} onChange={set} />{label}
    </label>);

  return (
    <div onClick={() => chrome.setOptions(false)}
      style={{ position: "fixed", inset: 0, zIndex: 962, background: "rgba(0,0,0,0.22)",
               font: `${ui.size}px ${ui.font}`, color: ui.fg }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="acad-options"
        style={{ position: "absolute", left: "50%", top: "12%", transform: "translateX(-50%)",
                 width: 520, background: ui.ribbon, border: `1px solid ${ui.menuLine}`,
                 boxShadow: "4px 5px 18px rgba(0,0,0,0.5)" }}>
        <div style={{ height: 26, display: "flex", alignItems: "center", padding: "0 10px",
                      fontWeight: 600, background: ui.title,
                      borderBottom: `1px solid ${ui.line}` }}>Options</div>
        <div style={{ display: "flex", gap: 2, padding: "6px 10px 0" }}>
          {([["display", "Display"], ["profiles", "Profiles"]] as const).map(([k, l]) => (
            <div key={k} data-testid={`acad-opt-tab-${k}`} onClick={() => setTab(k)}
              style={{ padding: "4px 14px", cursor: "pointer",
                       background: tab === k ? ui.panel : "transparent",
                       border: `1px solid ${tab === k ? ui.edge : "transparent"}`,
                       borderBottom: "none", color: tab === k ? ui.fg : ui.soft }}>{l}</div>))}
        </div>
        <div style={{ margin: "0 10px 10px", padding: 12, background: ui.panel,
                      border: `1px solid ${ui.edge}`, minHeight: 210 }}>
          {tab === "display" ? (
            <>
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <span>Color theme</span>
                <select data-testid="acad-opt-theme" value={cfg.theme}
                  onChange={(e) => chrome.set({ theme: e.target.value as "dark" | "light" })}
                  style={{ height: 22, minWidth: 120, background: ui.sunk, color: ui.fg,
                           border: `1px solid ${ui.edge}`, font: `${ui.size}px ${ui.font}` }}>
                  <option value="dark">Dark</option>
                  <option value="light">Light</option>
                </select>
              </div>
              {/* ⚠ AutoCAD's own Options ▸ Display ▸ Colors, reduced to the one row that
                  matters: the model space background. Its state comes from the HANDLER, not
                  from `cfg` — the canvas is PlanStudio's and the preference is shared with the
                  other two lanes, so the chrome config is the wrong place to keep it. */}
              {check("Black model space background",
                     !!norm(chrome.all["ui.canvasbg"]).on,
                     () => chrome.fire("ui.canvasbg"), "canvasbg")}
              <div style={{ color: ui.soft, margin: "10px 0 6px" }}>Window Elements</div>
              {check("Show menu bar", cfg.menubar, () => chrome.fire("ui.menubar"), "menubar")}
              {check("Show file tabs", cfg.filetabs, () => chrome.fire("ui.filetabs"), "filetabs")}
              {check("Show layout tabs", cfg.layouttabs, () => chrome.fire("ui.layouttabs"),
                     "layouttabs")}
              {check("Show command line", cfg.cmdline, () => chrome.fire("ui.cmdline"), "cmdline")}
              {check("Show ViewCube", cfg.viewcube, () => chrome.fire("view.viewcube"), "viewcube")}
              {check("Show navigation bar", cfg.navbar, () => chrome.fire("view.navbar"), "navbar")}
              {check("Show UCS icon", cfg.ucsicon, () => chrome.fire("view.ucsicon"), "ucsicon")}
              <div style={{ color: ui.soft, margin: "10px 0 6px" }}>Toolbars</div>
              {TOOLBARS.map((t) => check(t.label, !!cfg.toolbars[t.id],
                                         () => chrome.setTb(t.id, !cfg.toolbars[t.id]),
                                         t.id))}
            </>
          ) : (
            <>
              {/* ⚠⚠ **pdcad HAS ONE INTERFACE, AND THIS PANEL USED TO DENY IT.** It read "the
                  Rayon and SketchUp interfaces are chosen from the studio's command palette" and
                  offered a "Change interface…" button — both inherited from `/opt/dsn`, the app
                  that really does host three shells over one canvas. pdray and pdskp are
                  SEPARATE PRODUCTS (`/opt/pdray`, `/opt/pdskp`), not modes of this one.
                  ⚠ The button was also wired to nothing sensible: `onInterface` was
                  `ctx.openPalette`, which in pdcad opens the OPEN DRAWING dialog
                  (`Studio.tsx` -> `setOpenDlg(true)`). A control labelled "Change interface…"
                  that shows a file list is worse than no control. */}
              <div style={{ color: ui.soft, marginBottom: 6 }}>Interface</div>
              <div style={{ marginBottom: 8, lineHeight: 1.45 }}>
                pdcad has one interface: AutoCAD Drafting &amp; Annotation. Display, toolbars and
                profiles are on the tabs above.
              </div>
            </>)}
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, padding: "0 10px 10px" }}>
          <button onClick={() => chrome.setOptions(false)}
            style={{ padding: "4px 18px", cursor: "pointer", background: ui.panel, color: ui.fg,
                     border: `1px solid ${ui.edge}`, borderRadius: 2,
                     font: `${ui.size}px ${ui.font}` }}>OK</button>
        </div>
      </div>
    </div>);
}

/**
 * The View Manager — AutoCAD's VIEW command, and one of the cleanest exact mappings in the shell.
 *
 * ⚠⚠ AutoCAD's named views and the 3D view's saved SCENES are the same idea under two names: a
 * remembered camera you can restore by name. `Model3D` already stores them per plan, server-side,
 * with the visual style and the layer set alongside the camera — which is more than AutoCAD's
 * VIEW keeps, not less. So this manages them rather than inventing a second list.
 *
 * ⚠ It is honest about the one place ours is NARROWER: AutoCAD's VIEW also saves a 2D zoom
 * window in model space, and ours does not — a named view here is a 3D camera. "New" therefore
 * needs a 3D view to save FROM, and says so instead of writing an empty one.
 */
/** Close a modal on Escape.
 *
 * ⚠⚠ **NOT ONE OF THE FOUR DIALOGS IN THIS SHELL CLOSED ON ESCAPE.** Options, the View Manager,
 * Quick Select and Plot all opened fine and could only be dismissed by clicking the backdrop or
 * Cancel — and Escape is the reflex, in AutoCAD and in every other application. Found by the
 * live-button audit, and it had a second effect nobody would connect to it: a dialog left open
 * swallows every click after it, so 23 of the audit's own clicks timed out against a modal it
 * had opened and could not close.
 *
 * ⚠ Bound at the WINDOW in the capture phase, like the studio's Ctrl+K — the command line's
 * <input> would otherwise eat the keystroke while it has focus, which is exactly when a drafter
 * is most likely to press Escape.
 * ⚠ Registered only while `open`, so a closed dialog is not one more listener racing for the
 * key with whatever is actually on screen.
 */
function useEscape(open: boolean, close: () => void) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      e.stopPropagation();
      close();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, close]);
}

/** AutoCAD's FIND — search the drawing's notes.
 *
 * ⚠ AutoCAD's dialog also REPLACES text and searches block attributes and table cells. Ours
 * finds and selects, because that is what exists to search: notes and leaders. Replace would
 * need a second field and a rule for what "replace in 40 selected notes" means, and offering
 * a Replace box that only ever touched one of them would be worse than not offering it.
 * ⚠ It reports the COUNT rather than closing silently on no matches — "0 found" and "the
 * command is broken" look identical otherwise.
 */
export function AcadFind({ chrome, open, close, onFind }: {
  chrome: AcadChrome;
  open: boolean;
  close: () => void;
  onFind: (q: string) => number;
}) {
  const { ui } = chrome;
  const [q, setQ] = useState("");
  const [said, setSaid] = useState("");
  useEscape(open, close);
  if (!open) return null;
  const go = () => {
    const n = onFind(q);
    // ⚠ "text object", not "note". The wording came across with the dialog from the design
    // app, where the object IS a note; here it is a DXF TEXT or MTEXT and calling it anything
    // else teaches a drafter a word this product does not use anywhere else.
    setSaid(n ? `${n} text object${n === 1 ? "" : "s"} found and selected`
              : "no text matches that");
    if (n) close();
  };
  return (
    <div onClick={close}
      style={{ position: "fixed", inset: 0, zIndex: 963, background: "rgba(0,0,0,0.22)",
               font: `${ui.size}px ${ui.font}`, color: ui.fg }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="acad-find"
        style={{ position: "absolute", left: "50%", top: "18%", transform: "translateX(-50%)",
                 width: 380, background: ui.ribbon, border: `1px solid ${ui.menuLine}`,
                 boxShadow: "4px 5px 18px rgba(0,0,0,0.5)" }}>
        <div style={{ height: 26, display: "flex", alignItems: "center", padding: "0 10px",
                      fontWeight: 600, background: ui.title,
                      borderBottom: `1px solid ${ui.line}` }}>Find</div>
        <div style={{ padding: 10, display: "grid", gap: 8 }}>
          <label style={{ color: ui.soft }}>Find what</label>
          <input data-testid="acad-find-q" value={q} autoFocus
            onChange={(e) => { setQ(e.target.value); setSaid(""); }}
            onKeyDown={(e) => { if (e.key === "Enter") go(); }}
            style={{ height: 24, background: ui.sunk, color: ui.fg, width: "100%",
                     border: `1px solid ${ui.edge}`, padding: "0 6px",
                     font: `${ui.size}px ${ui.font}` }} />
          <div data-testid="acad-find-said" style={{ color: ui.soft, minHeight: 16 }}>{said}</div>
        </div>
        <div style={{ display: "flex", gap: 6, padding: "0 10px 12px", justifyContent: "flex-end" }}>
          <button data-testid="acad-find-go" onClick={go}
            style={{ padding: "4px 14px", cursor: "pointer", background: ui.accent, color: "#fff",
                     border: "none", borderRadius: 2, font: `${ui.size}px ${ui.font}` }}>Find</button>
          <button data-testid="acad-find-cancel" onClick={close}
            style={{ padding: "4px 14px", cursor: "pointer", background: ui.panel, color: ui.fg,
                     border: `1px solid ${ui.edge}`, borderRadius: 2,
                     font: `${ui.size}px ${ui.font}` }}>Close</button>
        </div>
      </div>
    </div>);
}

/** AutoCAD's QSELECT — Quick Select, cut to the two filters this drawing can actually answer.
 *
 * ⚠⚠ **NOT A TRANSCRIPTION OF AUTOCAD'S DIALOG, AND THAT IS THE ONE PLACE IN THIS SHELL WHERE
 * COPYING IT WOULD BE WRONG.** AutoCAD's QSELECT has Object type, Properties, Operator and
 * Value, and its Property list is whatever that entity carries — Colour, Linetype, Lineweight,
 * Thickness, forty of them on a polyline. Here pen properties come FROM THE LAYER, so a
 * "Colour = red" filter would be a filter on a field no object owns. Offering it would produce
 * a dialog that always selects nothing, which is worse than not offering it.
 *
 * ⚠ The two pickers are built from what is IN the drawing, not from the vocabulary — a filter
 * for a shape kind nobody has drawn can only ever select nothing, and a drafter reading an empty
 * result cannot tell that from a broken command.
 */
export function AcadQSelect({ chrome, open, close, kinds, wallKinds, hasBlocks, layers, onGo }: {
  chrome: AcadChrome;
  open: boolean;
  close: () => void;
  kinds: string[];
  wallKinds: string[];
  hasBlocks: boolean;
  layers: { id: number; name: string }[];
  onGo: (f: { type?: string; layer?: number | null; wallKind?: string }) => void;
}) {
  const { ui } = chrome;
  const [type, setType] = useState("");
  const [layer, setLayer] = useState("");
  useEscape(open, close);
  if (!open) return null;
  const field = { height: 22, background: ui.sunk, color: ui.fg, width: "100%",
                  border: `1px solid ${ui.edge}`, font: `${ui.size}px ${ui.font}` };
  const wall = type.startsWith("wall:");
  return (
    <div onClick={close}
      style={{ position: "fixed", inset: 0, zIndex: 963, background: "rgba(0,0,0,0.22)",
               font: `${ui.size}px ${ui.font}`, color: ui.fg }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="acad-qselect"
        style={{ position: "absolute", left: "50%", top: "16%", transform: "translateX(-50%)",
                 width: 380, background: ui.ribbon, border: `1px solid ${ui.menuLine}`,
                 boxShadow: "4px 5px 18px rgba(0,0,0,0.5)" }}>
        <div style={{ height: 26, display: "flex", alignItems: "center", padding: "0 10px",
                      fontWeight: 600, background: ui.title,
                      borderBottom: `1px solid ${ui.line}` }}>Quick Select</div>
        <div style={{ padding: 10, display: "grid", gap: 8 }}>
          <label style={{ color: ui.soft }}>Object type</label>
          <select data-testid="acad-qsel-type" value={type} style={field as any}
            onChange={(e) => setType(e.target.value)}>
            <option value="">Any linework</option>
            {kinds.map((k) => <option key={k} value={k}>{k}</option>)}
            {wallKinds.map((k) => <option key={`w${k}`} value={`wall:${k}`}>wall — {k}</option>)}
            {/* ⚠ "block reference", not "furniture" — same reason as FIND's wording above.
                A BLOCK in a CAD drawing is a title block, a door, a north point or a detail
                bubble as often as it is a chair. */}
            {hasBlocks ? <option value="block">block references (INSERT)</option> : null}
          </select>
          <label style={{ color: ui.soft }}>On layer</label>
          <select data-testid="acad-qsel-layer" value={layer} disabled={wall || type === "block"}
            style={{ ...field, opacity: wall || type === "block" ? 0.4 : 1 } as any}
            onChange={(e) => setLayer(e.target.value)}>
            <option value="">Any layer</option>
            {layers.map((l) => <option key={l.id} value={String(l.id)}>{l.name}</option>)}
          </select>
          {/* ⚠ Says WHY the layer picker is dead rather than just dimming it — the same rule the
              ribbon's greys follow. A wall's layer is decided by its kind, so the two filters
              would contradict each other. */}
          {wall || type === "block" ? (
            <div style={{ color: ui.soft, fontSize: ui.size - 1 }}>
              {wall ? "a wall's layer comes from its kind — the two filters would disagree"
                    : "a block reference sits on its own layer, not its definition's"}
            </div>) : null}
        </div>
        <div style={{ display: "flex", gap: 6, padding: "0 10px 12px", justifyContent: "flex-end" }}>
          <button data-testid="acad-qsel-go"
            onClick={() => {
              onGo(type.startsWith("wall:") ? { type: "wall", wallKind: type.slice(5) }
                : type === "block" ? { type: "block" }
                : { type: type || undefined, layer: layer ? Number(layer) : null });
              close();
            }}
            style={{ padding: "4px 14px", cursor: "pointer", background: ui.accent, color: "#fff",
                     border: "none", borderRadius: 2, font: `${ui.size}px ${ui.font}` }}>Select</button>
          <button data-testid="acad-qsel-cancel" onClick={close}
            style={{ padding: "4px 14px", cursor: "pointer", background: ui.panel, color: ui.fg,
                     border: `1px solid ${ui.edge}`, borderRadius: 2,
                     font: `${ui.size}px ${ui.font}` }}>Cancel</button>
        </div>
      </div>
    </div>);
}

export function AcadViewManager({ chrome, open, close, scenes, current, cam3d,
                                  onSet, onNew, onDelete }: {
  chrome: AcadChrome;
  open: boolean;
  close: () => void;
  scenes: { name: string }[];
  current: string;
  cam3d: boolean;
  onSet: (name: string) => void;
  onNew: (name: string) => void;
  onDelete: (name: string) => void;
}) {
  const { ui } = chrome;
  const [pick, setPick] = useState<string>(current);
  const [naming, setNaming] = useState("");
  useEscape(open, close);
  if (!open) return null;
  const btn = (label: string, go: () => void, off?: string, primary = false) => (
    <button data-testid={`acad-view-${label.toLowerCase().replace(/[^a-z]+/g, "")}`}
      disabled={!!off} title={off ? `⚠ ${off}` : ""} data-acad-off={off ? "1" : undefined}
      onClick={go}
      style={{ padding: "4px 14px", cursor: off ? "not-allowed" : "pointer",
               background: off ? ui.ribbon : primary ? ui.accent : ui.panel,
               color: off ? ui.soft : primary ? "#fff" : ui.fg,
               border: primary && !off ? "none" : `1px solid ${ui.edge}`, borderRadius: 2,
               font: `${ui.size}px ${ui.font}` }}>{label}</button>);

  return (
    <div onClick={close}
      style={{ position: "fixed", inset: 0, zIndex: 963, background: "rgba(0,0,0,0.22)",
               font: `${ui.size}px ${ui.font}`, color: ui.fg }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="acad-viewmgr"
        style={{ position: "absolute", left: "50%", top: "12%", transform: "translateX(-50%)",
                 width: 520, background: ui.ribbon, border: `1px solid ${ui.menuLine}`,
                 boxShadow: "4px 5px 18px rgba(0,0,0,0.5)" }}>
        <div style={{ height: 26, display: "flex", alignItems: "center", padding: "0 10px",
                      fontWeight: 600, background: ui.title,
                      borderBottom: `1px solid ${ui.line}` }}>View Manager</div>
        <div style={{ padding: 10 }}>
          <div style={{ color: ui.soft, marginBottom: 6 }}>Views</div>
          <div data-testid="acad-view-list"
            style={{ height: 168, overflowY: "auto", background: ui.sunk,
                     border: `1px solid ${ui.edge}`, marginBottom: 8 }}>
            {!scenes.length ? (
              <div style={{ padding: 10, color: ui.soft, lineHeight: 1.45 }}>
                This drawing has no named views yet. Orbit to the viewpoint you want and
                press New.
              </div>
            ) : scenes.map((sc) => (
              <div key={sc.name} data-testid={`acad-view-row-${sc.name}`}
                onClick={() => setPick(sc.name)} onDoubleClick={() => { onSet(sc.name); close(); }}
                style={{ padding: "3px 9px", cursor: "pointer",
                         background: pick === sc.name ? ui.hover : "transparent",
                         color: sc.name === current ? ui.accent : ui.fg }}>
                {sc.name}{sc.name === current ? "   ·  current" : ""}
              </div>))}
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4 }}>
            <input data-testid="acad-view-name" value={naming} placeholder="New view name…"
              onChange={(e) => setNaming(e.target.value)}
              style={{ flex: 1, height: 22, background: ui.sunk, color: ui.fg,
                       border: `1px solid ${ui.edge}`, padding: "0 6px",
                       font: `${ui.size}px ${ui.font}` }} />
            {btn("New", () => { onNew(naming.trim()); setNaming(""); },
                 !cam3d ? "a named view saves a 3D camera — orbit first (3DORBIT)"
                   : !naming.trim() ? "type a name for the view" : undefined)}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, padding: "0 10px 12px" }}>
          {btn("Set Current", () => { onSet(pick); close(); },
               pick ? undefined : "select a view in the list", true)}
          {btn("Delete", () => { onDelete(pick); setPick(""); },
               pick ? undefined : "select a view in the list")}
          <div style={{ flex: 1 }} />
          {btn("Close", close)}
        </div>
      </div>
    </div>);
}

/**
 * The Plot dialog — AutoCAD's own layout, with every control that drives a real query parameter
 * live and every control that does not GREYED WITH ITS REASON, exactly like the ribbon.
 *
 * ⚠⚠ This dialog is where the "grey teaches you something true" rule gets its hardest test,
 * because half of AutoCAD's Plot dialog is about a PLOTTER — a device, a driver, a plot style
 * table, a paper tray, an offset from the corner of a physical sheet. We plot to a PDF. Faking
 * a plotter list would be the same lie as a live button that does nothing, and dropping those
 * rows would quietly claim AutoCAD's dialog has fewer decisions in it than it does. So they
 * stay, greyed, saying what actually happens instead.
 *
 * ⚠ The four content switches are OURS and are labelled as ours. AutoCAD's Plot options row
 * (lineweights, transparency, plot styles, paperspace last) is about pen tables we do not have;
 * furniture / dimensions / schedules / room colour are real arguments to `to_pdf` and a drafter
 * issuing a set needs them. Same reasoning as the PDVN ribbon tab: a vendor extension announced
 * as one is honest, an approximation wearing AutoCAD's label is not.
 */
export function AcadPlot({ chrome, what, cfg, set, close, onPlot, sheets, busy, onSheet }: {
  chrome: AcadChrome;
  /** "drawing" = PLOT (this drawing), "set" = PUBLISH (the composed sheet set). */
  what: "drawing" | "set" | null;
  cfg: PlotCfg;
  set: (patch: Partial<PlotCfg>) => void;
  close: () => void;
  onPlot: (preview: boolean) => void;
  /** How many composed pages the drawing has — PUBLISH needs at least one. */
  sheets: number | null;
  busy: boolean;
  /** The LAYOUT currently open, if any. ⚠⚠ A layout plots at its own paper size and 1:1 by
   *  definition — paper space IS millimetres on paper — so the sheet, orientation and scale
   *  fields below do nothing while one is open. Saying so beats three live-looking controls
   *  that are quietly ignored, which is how a drafter comes to believe the plot is broken. */
  onSheet?: string | null;
}) {
  const { ui } = chrome;
  // ⚠ `what` is the open flag here — null means closed. Same pattern, different spelling,
  // which is why the hook takes a boolean rather than reaching for a field by name.
  useEscape(what !== null, close);
  if (!what) return null;
  const set_ = what === "set";
  const noPages = set_ && sheets === 0;

  const row = (label: string, body: React.ReactNode, note?: string) => (
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
      <span style={{ width: 116, color: note ? ui.soft : ui.fg, flex: "none" }}>{label}</span>
      <div style={{ flex: 1, minWidth: 0 }}>{body}</div>
    </div>);

  const sel = (testid: string, value: any, opts: [any, string][], on: (v: any) => void,
               off?: string) => (
    <select data-testid={`acad-plot-${testid}`} value={value} disabled={!!off} title={off || ""}
      onChange={(e) => on(e.target.value)}
      style={{ width: "100%", height: 22, background: off ? ui.ribbon : ui.sunk,
               color: off ? ui.soft : ui.fg, border: `1px solid ${ui.edge}`,
               font: `${ui.size}px ${ui.font}`, cursor: off ? "not-allowed" : "pointer" }}>
      {opts.map(([v, l]) => <option key={String(v)} value={v}>{l}</option>)}
    </select>);

  /** A control we do not drive. It keeps AutoCAD's label and says what happens instead. */
  const dead = (testid: string, label: string, why: string) => (
    <div data-testid={`acad-plot-${testid}`} data-acad-off="1" title={`⚠ ${why}`}
      style={{ height: 22, lineHeight: "20px", padding: "0 6px", background: ui.ribbon,
               color: ui.soft, border: `1px solid ${ui.edge}`, cursor: "not-allowed",
               whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
      {label}</div>);

  const tick = (testid: string, label: string, on: boolean, go: () => void, note?: string) => (
    <label data-testid={`acad-plot-${testid}`} title={note}
      style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer",
               padding: "2px 0" }}>
      <input type="checkbox" checked={on} onChange={go} />{label}
      {/* ⚠ The reason beside the control, not in a tooltip alone — a plot option whose effect
          is not obvious is one a drafter leaves at its default forever. */}
      {note ? <span style={{ color: ui.soft, fontSize: ui.size - 1 }}>— {note}</span> : null}
    </label>);

  const group = (title: string, body: React.ReactNode) => (
    <fieldset style={{ border: `1px solid ${ui.edge}`, margin: "0 0 10px", padding: "6px 10px 8px",
                       minWidth: 0 }}>
      <legend style={{ padding: "0 5px", color: ui.soft }}>{title}</legend>
      {body}
    </fieldset>);

  return (
    <div onClick={close}
      style={{ position: "fixed", inset: 0, zIndex: 963, background: "rgba(0,0,0,0.22)",
               font: `${ui.size}px ${ui.font}`, color: ui.fg }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="acad-plot"
        style={{ position: "absolute", left: "50%", top: "7%", transform: "translateX(-50%)",
                 width: 660, maxHeight: "86%", overflowY: "auto", background: ui.ribbon,
                 border: `1px solid ${ui.menuLine}`, boxShadow: "4px 5px 18px rgba(0,0,0,0.5)" }}>
        <div style={{ height: 26, display: "flex", alignItems: "center", padding: "0 10px",
                      fontWeight: 600, background: ui.title,
                      borderBottom: `1px solid ${ui.line}`, position: "sticky", top: 0 }}>
          {set_ ? "Publish" : `Plot — ${onSheet || "Model"}`}</div>

        <div style={{ padding: 10 }}>
          {!set_ && onSheet ? (
            <div data-testid="acad-plot-onsheet"
              style={{ marginBottom: 10, padding: "6px 9px", background: ui.sunk,
                       border: `1px solid ${ui.edge}`, color: ui.soft, lineHeight: 1.6 }}>
              Plotting layout <b style={{ color: ui.fg }}>{onSheet}</b> — at its own paper size,
              at 1:1. Paper space is millimetres on paper, so the sheet, orientation and scale
              below do not apply. Switch to the <b style={{ color: ui.fg }}>Model</b> tab to use
              them.
            </div>) : null}
          {group("Page setup", <>
            {row("Name", dead("pagesetup", "<None>",
              "named page setups are not stored — these settings are remembered per browser, " +
              "per drawing-independent, and reused on the next plot"))}
          </>)}

          {group("Printer/plotter", <>
            {row("Name", dead("device", "PDVN PDF — built in",
              "the drawing plots to a PDF file; there is no plotter list to choose from"))}
            {row("Plot to file", dead("tofile", "Always",
              "every plot is a file — PLOT opens it in a tab, and your PDF viewer prints it"))}
            {row("Copies", dead("copies", "1",
              "one PDF is produced; print as many copies as you like from the viewer"))}
          </>)}

          {group("Paper size", <>
            {row("Paper", sel("sheet", cfg.sheet,
              PLOT_SHEETS.map((k) => [k, k] as [any, string]), (v) => set({ sheet: v })))}
            {row("Orientation", sel("orientation", cfg.orientation, [
              ["auto", "Auto — whichever fits the drawing"],
              ["landscape", "Landscape"], ["portrait", "Portrait"],
            ], (v) => set({ orientation: v })))}
          </>)}

          {group("Plot area", <>
            {row("What to plot", dead("area", "Extents",
              "the sheet is always laid out around everything in the drawing. Display, Limits " +
              "and Window are not built — crop a viewport instead, on the drawing's data " +
              "sheet ▸ Sheets"))}
            {row("Plot offset", dead("offset", "Centred on the frame",
              "the drawing is centred inside the frame automatically; there is no offset to set"))}
          </>)}

          {group("Plot scale", <>
            {tick("fit", "Fit to paper", cfg.fit, () => set({ fit: !cfg.fit }))}
            {row("Scale", sel("scale", cfg.scale,
              PLOT_SCALES.map((n) => [n, `1 : ${n}`] as [any, string]),
              (v) => set({ scale: Number(v) }),
              cfg.fit ? "untick Fit to paper to plot at a fixed scale" : undefined))}
          </>)}

          {/* ⚠⚠ **THESE FOUR WERE THE OTHER APP'S SEMANTIC LAYERS AND MEANT NOTHING HERE.**
              "Furniture", "Dimensions", "Schedules" and "Room colour" are categories a BUILDING
              model has; pdcad's document is entities on layers with no semantics — see
              `models.py`'s header — so all four were live-looking checkboxes that changed
              nothing about the sheet. The honest replacement is the thing that DOES control what
              is plotted, and a sentence saying where it lives. */}
          {group("What gets plotted", <>
            <div style={{ color: ui.soft, marginBottom: 5, lineHeight: 1.45 }}>
              Everything visible on screen. A layer turned off in the Layer Properties Manager is
              left off the sheet — that is the control, and it is the same one AutoCAD uses.
            </div>
            {tick("colour", "Plot in colour", cfg.colour, () => set({ colour: !cfg.colour }),
                  "off plots black on white, which is what a drawing is")}
          </>)}

          {set_ ? (
            <div data-testid="acad-plot-setnote"
              style={{ padding: "6px 9px", marginBottom: 8, lineHeight: 1.45,
                       background: noPages ? "#4a3a1e" : ui.panel,
                       border: `1px solid ${noPages ? "#7a6030" : ui.edge}` }}>
              {sheets === null ? "Checking what sheets this drawing has…"
                : noPages
                  ? "This drawing has no composed sheets yet, so there is nothing to publish. "
                    + "Compose them on the drawing's data sheet ▸ Sheets, then publish."
                  : `Publishing all ${sheets} composed sheet${sheets === 1 ? "" : "s"} as one PDF. `
                    + "The paper size, scale and orientation above belong to the single-sheet "
                    + "plot; a composed sheet carries its own."}
            </div>) : null}
        </div>

        <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, padding: "0 10px 12px" }}>
          <button data-testid="acad-plot-preview" disabled={busy || noPages}
            onClick={() => onPlot(true)}
            style={{ padding: "4px 16px", cursor: busy || noPages ? "not-allowed" : "pointer",
                     background: ui.panel, color: busy || noPages ? ui.soft : ui.fg,
                     border: `1px solid ${ui.edge}`, borderRadius: 2,
                     font: `${ui.size}px ${ui.font}` }}>Preview…</button>
          <div style={{ flex: 1 }} />
          <button data-testid="acad-plot-ok" disabled={busy || noPages}
            onClick={() => onPlot(false)}
            style={{ padding: "4px 22px", cursor: busy || noPages ? "not-allowed" : "pointer",
                     background: busy || noPages ? ui.panel : ui.accent,
                     color: busy || noPages ? ui.soft : "#fff",
                     border: busy || noPages ? `1px solid ${ui.edge}` : "none", borderRadius: 2,
                     font: `${ui.size}px ${ui.font}` }}>
            {busy ? "Plotting…" : "OK"}</button>
          <button data-testid="acad-plot-cancel" onClick={close}
            style={{ padding: "4px 18px", cursor: "pointer", background: ui.panel, color: ui.fg,
                     border: `1px solid ${ui.edge}`, borderRadius: 2,
                     font: `${ui.size}px ${ui.font}` }}>Cancel</button>
        </div>
      </div>
    </div>);
}

/** AutoCAD's five layer dialogs, which are one dialog.
 *
 * ⚠⚠ **LAYMCH, COPYTOLAYER, LAYMRG, LAYDEL AND LAYWALK ALL ASK "WHICH LAYER?"** They differ in
 * the verb, in whether a second answer is needed, and in whether the answer is destructive.
 * Five near-identical modals would be five places for the layer list to be built slightly
 * differently — and the list is the part that matters, because a layer missing from one of them
 * is a command that silently cannot reach it.
 *
 * ⚠ WALK is the one that is not a question: it turns layers on one at a time while it is open
 * and puts them ALL back on close, including if the dialog is escaped. A walk that left the
 * drawing with one layer showing would be a destructive command wearing a browsing command's
 * name.
 *
 * ⚠ STATES are kept in `localStorage`, per drawing, and the dialog says so in the window. A
 * layer state is arguably drawing data and belongs on the server; putting it there needs a
 * model, a migration and an endpoint, and until that exists the honest thing is to ship the
 * useful version with its limit written where it is read, not to hide the command.
 */
export function AcadLayerTool({ chrome, dlg, close, layers, counts, onPatch, onMerge,
                               onErase }: {
  chrome: AcadChrome;
  dlg: { mode: string; title: string; verb?: string; hint?: string;
         onPick?: (a: string, b?: string) => void } | null;
  close: () => void;
  layers: any[];
  /** How many objects sit on a layer — Merge and Delete both have to say it before they run. */
  counts: (name: string) => number;
  onPatch: (names: string[], patch: any, remember?: boolean) => number;
  /** LAYMRG — move every object off `from` onto `to`, then delete the empty layer. Both halves
   *  belong together: a merge that moved the geometry and left the layer behind has not merged
   *  anything, it has only emptied something. */
  onMerge: (from: string, to: string) => void;
  onErase: (name: string) => void;
}) {
  const { ui } = chrome;
  const [a, setA] = useState("");
  const [b, setB] = useState("");
  const [walk, setWalk] = useState(0);
  const [states, setStates] = useState<Record<string, any>>({});
  const [newName, setNewName] = useState("");
  const key = `pderp.design.acad.layerstates`;
  const open = !!dlg;
  useEscape(open, close);

  const names = useMemo(() => layers.map((l) => l.name), [layers]);

  // ⚠ Re-seeded every time the dialog opens, not once: the layer list changes under it (a merge
  // deletes one) and a stale `a` would point at a layer that is gone.
  useEffect(() => {
    if (!open) return;
    setA(names[0] || ""); setB(names[1] || names[0] || ""); setWalk(0); setNewName("");
    try { setStates(JSON.parse(localStorage.getItem(key) || "{}")); } catch { setStates({}); }
  }, [open, dlg?.mode]);

  // ⚠⚠ **THE WALK RESTORES ON UNMOUNT, NOT ON THE CLOSE BUTTON.** Escape, a click on the
  // backdrop and the X all unmount without running an onClick, and every one of them would
  // otherwise leave the drawing showing one layer with no obvious way back.
  const walking = open && dlg?.mode === "walk";
  const before = useRef<{ names: string[]; on: boolean[] } | null>(null);
  useEffect(() => {
    if (!walking) return;
    before.current = { names, on: layers.map((l) => !!l.visible) };
    return () => {
      const w = before.current;
      if (!w) return;
      const back = w.names.filter((_, i) => w.on[i]);
      const hide = w.names.filter((_, i) => !w.on[i]);
      if (back.length) onPatch(back, { visible: true }, false);
      if (hide.length) onPatch(hide, { visible: false }, false);
    };
  }, [walking]);

  const step = (i: number) => {
    const n = names.length;
    if (!n) return;
    const at = ((i % n) + n) % n;
    setWalk(at);
    onPatch(names.filter((_, j) => j !== at), { visible: false }, false);
    onPatch([names[at]], { visible: true }, false);
  };

  if (!open || !dlg) return null;
  const row = { height: 24, background: ui.sunk, color: ui.fg, width: "100%",
                border: `1px solid ${ui.edge}`, padding: "0 6px",
                font: `${ui.size}px ${ui.font}` } as React.CSSProperties;
  const btn = (bg: string, fg: string) => ({
    padding: "4px 16px", cursor: "pointer", background: bg, color: fg,
    border: bg === ui.accent || bg === "#9E1B32" ? "none" : `1px solid ${ui.edge}`,
    borderRadius: 2, font: `${ui.size}px ${ui.font}` } as React.CSSProperties);
  const danger = dlg.mode === "delete" || dlg.mode === "merge";

  const go = () => {
    if (dlg.mode === "pick") { dlg.onPick?.(a); close(); return; }
    if (dlg.mode === "merge") {
      if (a === b) return;
      onMerge(a, b);
      close();
      return;
    }
    if (dlg.mode === "delete") { onErase(a); close(); return; }
    close();
  };

  const saveState = () => {
    const n = newName.trim();
    if (!n) return;
    const next = { ...states,
      [n]: layers.map((l) => ({ name: l.name, visible: !!l.visible, locked: !!l.locked })) };
    setStates(next); setNewName("");
    try { localStorage.setItem(key, JSON.stringify(next)); } catch { /* private mode */ }
  };
  const restoreState = (n: string) => {
    const snap: any[] = states[n] || [];
    const on = snap.filter((q) => q.visible).map((q) => q.name);
    const off = snap.filter((q) => !q.visible).map((q) => q.name);
    const lock = snap.filter((q) => q.locked).map((q) => q.name);
    const free = snap.filter((q) => !q.locked).map((q) => q.name);
    if (on.length) onPatch(on, { visible: true });
    if (off.length) onPatch(off, { visible: false }, false);
    if (lock.length) onPatch(lock, { locked: true }, false);
    if (free.length) onPatch(free, { locked: false }, false);
    close();
  };
  const dropState = (n: string) => {
    const next = { ...states }; delete next[n];
    setStates(next);
    try { localStorage.setItem(key, JSON.stringify(next)); } catch { /* private mode */ }
  };

  return (
    <div onClick={close}
      style={{ position: "fixed", inset: 0, zIndex: 966, background: "rgba(0,0,0,0.22)",
               font: `${ui.size}px ${ui.font}`, color: ui.fg }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="acad-layertool"
        style={{ position: "absolute", left: "50%", top: "16%", transform: "translateX(-50%)",
                 width: 400, background: ui.ribbon, border: `1px solid ${ui.menuLine}`,
                 boxShadow: "4px 5px 18px rgba(0,0,0,0.5)" }}>
        <div style={{ height: 26, display: "flex", alignItems: "center", padding: "0 10px",
                      fontWeight: 600, background: ui.title,
                      borderBottom: `1px solid ${ui.line}` }}>{dlg.title}</div>
        <div style={{ padding: 10, display: "grid", gap: 8 }}>
          {dlg.hint ? <div style={{ color: ui.soft }}>{dlg.hint}</div> : null}

          {dlg.mode === "walk" ? (
            <>
              <div data-testid="acad-layerwalk-name"
                style={{ fontWeight: 600, fontSize: ui.size + 2 }}>
                {names[walk] || "— no layers —"}</div>
              <div style={{ color: ui.soft }}>
                {names.length ? `${walk + 1} of ${names.length} · `
                  + `${counts(names[walk])} object${counts(names[walk]) === 1 ? "" : "s"}` : ""}
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <button data-testid="acad-layerwalk-prev" onClick={() => step(walk - 1)}
                  style={btn(ui.panel, ui.fg)}>‹ Previous</button>
                <button data-testid="acad-layerwalk-next" onClick={() => step(walk + 1)}
                  style={btn(ui.panel, ui.fg)}>Next ›</button>
              </div>
            </>
          ) : null}

          {dlg.mode === "states" ? (
            <>
              <div style={{ maxHeight: 150, overflow: "auto",
                            border: `1px solid ${ui.edge}`, background: ui.sunk }}>
                {Object.keys(states).length === 0
                  ? <div style={{ padding: 8, color: ui.soft }}>No saved states.</div>
                  : Object.keys(states).map((n) => (
                    <div key={n} style={{ display: "flex", alignItems: "center", gap: 6,
                                          padding: "3px 6px" }}>
                      <span style={{ flex: 1 }}>{n}</span>
                      <button data-testid={`acad-lstate-restore-${n}`}
                        onClick={() => restoreState(n)}
                        style={btn(ui.panel, ui.fg)}>Restore</button>
                      <span onClick={() => dropState(n)} title="Delete this state"
                        style={{ cursor: "pointer", color: ui.soft, padding: "0 4px" }}>✕</span>
                    </div>))}
              </div>
              <div style={{ display: "flex", gap: 6 }}>
                <input data-testid="acad-lstate-name" value={newName} placeholder="New state name"
                  onChange={(e) => setNewName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") saveState(); }} style={row} />
                <button data-testid="acad-lstate-save" onClick={saveState}
                  style={btn(ui.accent, "#fff")}>Save</button>
              </div>
              <div style={{ color: ui.soft }}>
                ⚠ Saved in this browser, not in the drawing — a colleague opening the same plan
                will not see these.</div>
            </>
          ) : null}

          {dlg.mode === "pick" || dlg.mode === "merge" || dlg.mode === "delete" ? (
            <>
              <label style={{ color: ui.soft }}>
                {dlg.mode === "merge" ? "Merge this layer…" : "Layer"}</label>
              <select data-testid="acad-layertool-a" value={a} autoFocus
                onChange={(e) => setA(e.target.value)} style={row}>
                {names.map((n) => <option key={n} value={n}>
                  {dlg.mode === "pick" ? n : `${n}  (${counts(n)})`}</option>)}
              </select>
              {dlg.mode === "merge" ? (
                <>
                  <label style={{ color: ui.soft }}>…into this one</label>
                  <select data-testid="acad-layertool-b" value={b}
                    onChange={(e) => setB(e.target.value)} style={row}>
                    {names.map((n) => <option key={n} value={n}>{n}</option>)}
                  </select>
                </>) : null}
              {danger ? (
                <div data-testid="acad-layertool-warn" style={{ color: "#e0a03a" }}>
                  {dlg.mode === "delete"
                    ? `${counts(a)} object${counts(a) === 1 ? "" : "s"} on ${a} will be erased.`
                    : (a === b ? "Pick two different layers."
                       : `${counts(a)} object${counts(a) === 1 ? "" : "s"} move to ${b}.`)}
                </div>) : null}
            </>
          ) : null}
        </div>
        <div style={{ display: "flex", gap: 6, padding: "0 10px 12px", justifyContent: "flex-end" }}>
          {dlg.verb ? (
            <button data-testid="acad-layertool-ok" onClick={go}
              disabled={dlg.mode === "merge" && a === b}
              style={btn(dlg.mode === "merge" && a === b ? ui.panel
                         : (danger ? "#9E1B32" : ui.accent), "#fff")}>{dlg.verb}</button>
          ) : null}
          <button data-testid="acad-layertool-close" onClick={close}
            style={btn(ui.panel, ui.fg)}>Close</button>
        </div>
      </div>
    </div>);
}

/** AutoCAD's INSERT ▸ Import and XATTACH — a client's DWG or DXF, into this drawing.
 *
 * ⚠⚠ **THE BACKEND FOR THIS HAS EXISTED THE WHOLE TIME AND NOTHING IN THE UI COULD REACH IT.**
 * `importer.import_cad` reads DWG and DXF, creates the layers it finds, follows XREFs one level
 * and NAMES the ones it cannot find — and `/design/plans/<id>/import_cad/` has been serving it.
 * Meanwhile INSERT ▸ Import was grey saying "DXF comes in on this tab", which was true of the
 * tab and false of every button on it. A working feature nobody can press is indistinguishable
 * from one that does not exist.
 *
 * ⚠⚠ **`replace` IS OFF, IS THE LAST FIELD, AND SAYS WHAT IT DELETES.** It erases every shape
 * and layer already on the plan. Defaulting it on — or labelling it "Replace existing" and
 * leaving a drafter to guess the scope — is how an afternoon's drafting disappears behind a
 * checkbox that sounded like it meant "overwrite the file".
 *
 * ⚠ The REPORT is the point of the dialog, not a courtesy. An import that silently succeeds
 * leaves a drafter to work out whether the 40 missing walls are the importer's fault, the
 * file's, or a layer that came in switched off. Counts, skipped entity types and unresolved
 * xrefs are all named.
 */
export function AcadImport({ chrome, open, close, onImport }: {
  chrome: AcadChrome;
  open: boolean;
  close: () => void;
  onImport: (file: File, opts: { unit_scale?: string; layout: string; replace: boolean })
    => Promise<any>;
}) {
  const { ui } = chrome;
  const [file, setFile] = useState<File | null>(null);
  const [units, setUnits] = useState("");
  const [layout, setLayout] = useState("model");
  const [replace, setReplace] = useState(false);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState<any>(null);
  const [err, setErr] = useState("");
  useEscape(open && !busy, close);
  useEffect(() => {
    if (!open) return;
    setFile(null); setUnits(""); setLayout("model"); setReplace(false);
    setReport(null); setErr(""); setBusy(false);
  }, [open]);
  if (!open) return null;

  const row = { height: 24, background: ui.sunk, color: ui.fg, width: "100%",
                border: `1px solid ${ui.edge}`, padding: "0 6px",
                font: `${ui.size}px ${ui.font}` } as React.CSSProperties;
  const btn = (bg: string, fg: string) => ({
    padding: "4px 18px", cursor: busy ? "wait" : "pointer", background: bg, color: fg,
    border: bg === ui.accent ? "none" : `1px solid ${ui.edge}`, borderRadius: 2,
    font: `${ui.size}px ${ui.font}` } as React.CSSProperties);

  const go = async () => {
    if (!file || busy) return;
    setBusy(true); setErr(""); setReport(null);
    try {
      const r = await onImport(file, { unit_scale: units || undefined, layout, replace });
      setReport(r?.report || r || {});
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || "Import failed.");
    } finally { setBusy(false); }
  };

  const made = report?.created || {};
  const skipped: Record<string, number> = report?.skipped || {};
  return (
    <div onClick={busy ? undefined : close}
      style={{ position: "fixed", inset: 0, zIndex: 967, background: "rgba(0,0,0,0.22)",
               font: `${ui.size}px ${ui.font}`, color: ui.fg }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="acad-import"
        style={{ position: "absolute", left: "50%", top: "13%", transform: "translateX(-50%)",
                 width: 440, background: ui.ribbon, border: `1px solid ${ui.menuLine}`,
                 boxShadow: "4px 5px 18px rgba(0,0,0,0.5)" }}>
        <div style={{ height: 26, display: "flex", alignItems: "center", padding: "0 10px",
                      fontWeight: 600, background: ui.title,
                      borderBottom: `1px solid ${ui.line}` }}>Import a drawing</div>
        <div style={{ padding: 10, display: "grid", gap: 8 }}>
          <label style={{ color: ui.soft }}>File (.dwg or .dxf, up to 120 MB)</label>
          <input data-testid="acad-import-file" type="file" accept=".dwg,.dxf" disabled={busy}
            onChange={(e) => { setFile(e.target.files?.[0] || null); setReport(null); }}
            style={{ ...row, height: "auto", padding: 4 }} />

          <label style={{ color: ui.soft }}>Units in the file</label>
          {/* ⚠ "Read from the file" first, because a DWG normally CARRIES its units ($INSUNITS)
              and overriding a correct value is how a 3 m wall arrives 3 mm long. The overrides
              are here for the files that record 0 = unitless, which is common enough to need
              an answer and rare enough not to be the default. */}
          <select data-testid="acad-import-units" value={units} disabled={busy}
            onChange={(e) => setUnits(e.target.value)} style={row}>
            <option value="">Read from the file</option>
            <option value="1">Millimetres</option>
            <option value="10">Centimetres</option>
            <option value="1000">Metres</option>
            <option value="25.4">Inches</option>
            <option value="304.8">Feet</option>
          </select>

          <label style={{ color: ui.soft }}>Which space</label>
          <select data-testid="acad-import-layout" value={layout} disabled={busy}
            onChange={(e) => setLayout(e.target.value)} style={row}>
            <option value="model">Model space</option>
            <option value="*">Model space and every layout</option>
          </select>

          <label style={{ display: "flex", gap: 6, alignItems: "center",
                          color: replace ? "#e0a03a" : ui.fg }}>
            <input data-testid="acad-import-replace" type="checkbox" checked={replace}
              disabled={busy} onChange={(e) => setReplace(e.target.checked)} />
            Erase this drawing's shapes and layers first
          </label>

          {err ? <div data-testid="acad-import-err" style={{ color: "#e0603a" }}>{err}</div> : null}

          {report ? (
            <div data-testid="acad-import-report"
              style={{ border: `1px solid ${ui.edge}`, background: ui.sunk, padding: 8,
                       display: "grid", gap: 3, maxHeight: 190, overflow: "auto" }}>
              <div style={{ fontWeight: 600 }}>
                {made.shapes || 0} shapes · {made.layers || 0} layers
                {report.unit_scale ? ` · ×${report.unit_scale} to mm` : ""}
              </div>
              {report.entities ? <div style={{ color: ui.soft }}>
                {report.entities} entities read{report.truncated ? " (file truncated — too many)"
                                                                 : ""}</div> : null}
              {Object.keys(skipped).length ? (
                <div style={{ color: ui.soft }}>
                  not read: {Object.entries(skipped).map(([k, v]) => `${k} ×${v}`).join(", ")}
                </div>) : null}
              {(report.xrefs_pulled || []).length ? (
                <div style={{ color: ui.soft }}>xrefs followed: {
                  (report.xrefs_pulled || []).map((x: any) =>
                    `${x.name} (${x.shapes})`).join(", ")}</div>) : null}
              {(report.xrefs_missing || []).length ? (
                <div style={{ color: "#e0a03a" }}>
                  ⚠ xrefs NOT found — this drawing is missing what they hold:{" "}
                  {(report.xrefs_missing || []).join(", ")}</div>) : null}
              {report.audit ? <div style={{ color: ui.soft }}>
                {report.audit} problem{report.audit === 1 ? "" : "s"} repaired while reading
              </div> : null}
            </div>) : null}
        </div>
        <div style={{ display: "flex", gap: 6, padding: "0 10px 12px",
                      justifyContent: "flex-end" }}>
          {report ? null : (
            <button data-testid="acad-import-ok" onClick={go} disabled={!file || busy}
              style={btn(!file || busy ? ui.panel : ui.accent, !file || busy ? ui.soft : "#fff")}>
              {busy ? "Reading…" : "Import"}</button>)}
          <button data-testid="acad-import-close" onClick={close} disabled={busy}
            style={btn(ui.panel, ui.fg)}>{report ? "Done" : "Cancel"}</button>
        </div>
      </div>
    </div>);
}

/** PDFIMPORT — a PDF's own vectors as real geometry.
 *
 * ⚠⚠ **TWO STEPS, AND THE FIRST ONE IS THE POINT.** Choosing the file PROBES it: how many pages,
 * whether it has vectors at all, and — read out of the title block — what scale it says it was
 * plotted at. A PDF carries no units and no scale as data, so a one-shot import would have to
 * guess, and a plan imported at half size is the CAD error that survives all the way to a site
 * measurement. The probe turns "what scale is this?", a question the drafter usually cannot
 * answer about somebody else's file, into a default they confirm.
 *
 * ⚠ A scan gets a different answer, not a worse import: with no vector paths there is nothing
 * to convert, and the dialog says so and points at the underlay, which is the right tool for it.
 */
export function AcadPdfImport({ chrome, open, close, onProbe, onImport }: {
  chrome: AcadChrome; open: boolean; close: () => void;
  onProbe: (file: File, page: number) => Promise<any>;
  onImport: (file: File, opts: { page: number; scale: number; replace: boolean;
                                 text: boolean; origin: string }) => Promise<any>;
}) {
  const { ui } = chrome;
  const [file, setFile] = useState<File | null>(null);
  const [page, setPage] = useState(1);
  const [info, setInfo] = useState<any>(null);
  const [scale, setScale] = useState("1");
  const [origin, setOrigin] = useState("ink");
  const [text, setText] = useState(true);
  const [replace, setReplace] = useState(false);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState<any>(null);
  const [err, setErr] = useState("");
  useEscape(open && !busy, close);
  useEffect(() => {
    if (!open) return;
    setFile(null); setPage(1); setInfo(null); setScale("1"); setOrigin("ink");
    setText(true); setReplace(false); setReport(null); setErr(""); setBusy(false);
  }, [open]);
  if (!open) return null;

  const row = { height: 24, background: ui.sunk, color: ui.fg, width: "100%",
                border: `1px solid ${ui.edge}`, padding: "0 6px",
                font: `${ui.size}px ${ui.font}` } as React.CSSProperties;
  const btn = (bg: string, fg: string) => ({
    padding: "4px 18px", cursor: busy ? "wait" : "pointer", background: bg, color: fg,
    border: bg === ui.accent ? "none" : `1px solid ${ui.edge}`, borderRadius: 2,
    font: `${ui.size}px ${ui.font}` } as React.CSSProperties);

  const probe = async (f: File | null, pg: number) => {
    if (!f) return;
    setBusy(true); setErr(""); setInfo(null); setReport(null);
    try {
      const r = await onProbe(f, pg);
      setInfo(r);
      setScale(String(r?.suggest_scale || 1));
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || "Could not read that PDF.");
    } finally { setBusy(false); }
  };

  const go = async () => {
    if (!file || busy) return;
    setBusy(true); setErr(""); setReport(null);
    try {
      setReport(await onImport(file, { page, scale: Number(scale) || 1, replace, text, origin }));
    } catch (e: any) {
      setErr(e?.response?.data?.detail || e?.message || "Import failed.");
    } finally { setBusy(false); }
  };

  const made = report?.created || {};
  return (
    <div onClick={busy ? undefined : close}
      style={{ position: "fixed", inset: 0, zIndex: 967, background: "rgba(0,0,0,0.22)",
               font: `${ui.size}px ${ui.font}`, color: ui.fg }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="acad-pdfimport"
        style={{ position: "absolute", left: "50%", top: "11%", transform: "translateX(-50%)",
                 width: 450, background: ui.ribbon, border: `1px solid ${ui.menuLine}`,
                 boxShadow: "4px 5px 18px rgba(0,0,0,0.5)" }}>
        <div style={{ height: 26, display: "flex", alignItems: "center", padding: "0 10px",
                      fontWeight: 600, background: ui.title,
                      borderBottom: `1px solid ${ui.line}` }}>Import a PDF as geometry</div>
        <div style={{ padding: 10, display: "grid", gap: 8 }}>
          <label style={{ color: ui.soft }}>File (.pdf, up to 120 MB)</label>
          <input data-testid="acad-pdf-file" type="file" accept=".pdf" disabled={busy}
            onChange={(e) => { const f = e.target.files?.[0] || null;
                               setFile(f); setPage(1); void probe(f, 1); }}
            style={{ ...row, height: "auto", padding: 4 }} />

          {info ? (
            <div data-testid="acad-pdf-probe"
              style={{ border: `1px solid ${ui.edge}`, background: ui.sunk, padding: 8,
                       display: "grid", gap: 3 }}>
              <div>{info.pages} page{info.pages === 1 ? "" : "s"} ·{" "}
                {info.size_mm?.[0]} × {info.size_mm?.[1]} mm ·{" "}
                {info.vectors ? `${info.segments.toLocaleString()} path segments`
                              : "no vector paths"}</div>
              {info.vectors ? null : (
                <div style={{ color: "#e0a03a" }}>
                  ⚠ This page is a scan. There is nothing to convert — place it as an underlay
                  (Insert ▸ Reference) and trace what you need.</div>)}
              {info.over_limit ? (
                <div style={{ color: "#e0a03a" }}>
                  ⚠ more than {info.max_items?.toLocaleString()} segments — too much to import
                  at once</div>) : null}
              {(info.ocg_layers || []).length ? (
                <div style={{ color: ui.soft }}>PDF layers: {info.ocg_layers.join(", ")}</div>
              ) : <div style={{ color: ui.soft }}>
                    no PDF layers — imported geometry is grouped by pen colour instead</div>}
              {(info.scales_found || []).length ? (
                <div style={{ color: ui.soft }}>the sheet says 1:{
                  (info.scales_found || []).join(", 1:")}</div>) : null}
            </div>) : null}

          {info && info.pages > 1 ? (<>
            <label style={{ color: ui.soft }}>Page</label>
            <input data-testid="acad-pdf-page" type="number" min={1} max={info.pages} value={page}
              disabled={busy} style={row}
              onChange={(e) => { const n = Number(e.target.value) || 1;
                                 setPage(n); void probe(file, n); }} /></>) : null}

          <label style={{ color: ui.soft }}>Plot scale — 1 : this</label>
          {/* ⚠ FREE TEXT, not a list. 1:50, 1:100 and 1:20 cover most sheets, and the one that
              is not on any list is the one that matters: a detail plotted at 1:7.5, or a page
              exported at "fit to paper", where the number has to be worked out from a known
              dimension. A list would make that case impossible. */}
          <input data-testid="acad-pdf-scale" value={scale} disabled={busy} style={row}
            onChange={(e) => setScale(e.target.value)} />

          <label style={{ color: ui.soft }}>Where the page's corner lands</label>
          <select data-testid="acad-pdf-origin" value={origin} disabled={busy} style={row}
            onChange={(e) => setOrigin(e.target.value)}>
            <option value="ink">At the drawing itself (ignore the sheet margin)</option>
            <option value="page">At the paper's top-left corner</option>
          </select>

          <label style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input data-testid="acad-pdf-text" type="checkbox" checked={text} disabled={busy}
              onChange={(e) => setText(e.target.checked)} />
            Bring the text across too
          </label>
          <label style={{ display: "flex", gap: 6, alignItems: "center",
                          color: replace ? "#e0a03a" : ui.fg }}>
            <input data-testid="acad-pdf-replace" type="checkbox" checked={replace}
              disabled={busy} onChange={(e) => setReplace(e.target.checked)} />
            Erase this drawing's shapes first
          </label>

          {err ? <div data-testid="acad-pdf-err" style={{ color: "#e0603a" }}>{err}</div> : null}
          {report ? (
            <div data-testid="acad-pdf-report"
              style={{ border: `1px solid ${ui.edge}`, background: ui.sunk, padding: 8,
                       display: "grid", gap: 3, maxHeight: 150, overflow: "auto" }}>
              <div style={{ fontWeight: 600 }}>
                {made.shapes || 0} shapes · {made.texts || 0} texts ·{" "}
                1 point = {report.mm_per_point} mm</div>
              {(report.layers || []).slice(0, 8).map((l: any) => (
                <div key={l.name} style={{ color: ui.soft }}>{l.name} — {l.shapes}</div>))}
              {report.note ? <div style={{ color: "#e0a03a" }}>⚠ {report.note}</div> : null}
            </div>) : null}
        </div>
        <div style={{ display: "flex", gap: 6, padding: "0 10px 12px",
                      justifyContent: "flex-end" }}>
          {report ? null : (
            <button data-testid="acad-pdf-ok" onClick={go}
              disabled={!file || busy || !info?.vectors || !!info?.over_limit}
              style={btn(!file || busy || !info?.vectors ? ui.panel : ui.accent,
                         !file || busy || !info?.vectors ? ui.soft : "#fff")}>
              {busy ? "Reading…" : "Import"}</button>)}
          <button data-testid="acad-pdf-close" onClick={close} disabled={busy}
            style={btn(ui.panel, ui.fg)}>{report ? "Done" : "Cancel"}</button>
        </div>
      </div>
    </div>);
}

/** VPORTS — a second view of the model, docked beside the drawing.
 *
 * ⚠⚠ **TWO VERTICAL IS THE ONLY CONFIGURATION THAT CAN EXIST HERE, AND THE REASON IS THE SEAM.**
 * AutoCAD tiles model space into 1, 2, 3 or 4 viewports, each with its own view direction. The
 * plan canvas belongs to `DesignStudioPage`, not to this shell — a shell gets `viewport` (which
 * REPLACES the canvas entirely) and `side` (a flex sibling beside it). So the drawing can appear
 * exactly once, and the only split that can hold it is one vertical division with the drawing on
 * the left. Three and Four Equal would need the page to hand out N canvases, which is a change to
 * the contract every shell shares rather than a change to this one.
 *
 * ⚠ It is a REAL two-viewport split in the way that matters: two views of one model, side by
 * side, live. The right pane is `Model3D` on the same plan, so orbiting it does not disturb the
 * drawing and drawing does not disturb it.
 *
 * ⚠⚠ **NEVER MOUNTED AT THE SAME TIME AS THE FULL-AREA 3D VIEW.** Both are `Model3D` on a
 * 2.3-million-triangle scene; two WebGL contexts on the same model is twice the VRAM for a
 * second copy of a picture already on screen. Turning the split on returns the camera to Top,
 * which is also what a drafter means by "put the plan back on the left".
 */
export function AcadViewportPane({ chrome, on, title, children }: {
  chrome: AcadChrome;
  on: boolean;
  title: string;
  children: React.ReactNode;
}) {
  const { ui, cfg } = chrome;
  if (!on || cfg.clean) return null;
  return (
    <div data-testid="acad-vport-pane"
      style={{ flex: "0 0 42%", minWidth: 260, display: "flex", flexDirection: "column",
               background: ui.canvas3d, borderLeft: `2px solid ${ui.accent}`,
               font: `${ui.size}px ${ui.font}`, color: ui.fg, overflow: "hidden" }}>
      {/* ⚠ The bar names WHICH viewport this is and how to close it. AutoCAD marks the ACTIVE
          viewport with a heavy border; ours is always the drawing, because that is the only one
          you can draw in — saying so is better than a border a drafter would test by clicking. */}
      <div style={{ height: 22, flex: "0 0 auto", display: "flex", alignItems: "center", gap: 6,
                    padding: "0 6px", background: ui.tabs, fontWeight: 600,
                    borderBottom: `1px solid ${ui.line}` }}>
        <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden",
                       textOverflow: "ellipsis" }}>{title}</span>
        <span style={{ color: ui.soft, fontWeight: 400 }}>drawing stays active</span>
        <span data-testid="acad-vport-close" title="Join viewports (VPORTS 1)"
          onClick={() => chrome.fire("view.vports.join")}
          style={{ cursor: "pointer", color: ui.soft, padding: "0 2px" }}>✕</span>
      </div>
      <div style={{ flex: 1, position: "relative", overflow: "hidden" }}>{children}</div>
    </div>);
}


/** ── AutoCAD's Solid tab: making a free-standing body ───────────────────────────────────────
 *
 * ⚠⚠ **A FORM, NOT A 3D PICKING GESTURE, AND THAT IS THE HONEST CHOICE HERE.** AutoCAD's BOX is
 * "pick a corner, pick the opposite corner, pick a height" — three picks in a 3D viewport with
 * live snapping to solids that already exist. This app's 3D view is a VIEWER with a few editing
 * gestures (move, push/pull, paint); it has no 3D construction plane and no 3D osnap, so a
 * three-pick BOX here would be three clicks that guess. Numbers typed once, then moved and
 * pushed with the gestures the viewer really has, is the version that does what it says.
 *
 * ⚠ Each kind asks ONLY for the fields it uses. A dialog that shows Radius greyed out next to
 * Width for a Box teaches the drafter to ignore half of it, and then they ignore the half that
 * mattered for a Cone.
 *
 * ⚠ The profile kinds (EXTRUDE, REVOLVE, LOFT, SWEEP) take no size at all except a height —
 * their shape comes from the plan shapes selected on the canvas, which is why the dialog names
 * the selection and refuses to open without one.
 */
export const BODY_FIELDS: Record<string, string[]> = {
  box: ["w_mm", "d_mm", "h_mm"],
  wedge: ["w_mm", "d_mm", "h_mm"],
  pyramid: ["w_mm", "d_mm", "h_mm", "r2_mm"],
  cylinder: ["r_mm", "h_mm"],
  cone: ["r_mm", "r2_mm", "h_mm"],
  sphere: ["r_mm"],
  torus: ["r_mm", "r2_mm"],
  extrude: ["h_mm"],
  polysolid: ["h_mm"],
  revolve: ["sweep"],
  loft: ["h_mm"],
  sweep: ["h_mm"],
};

const BODY_LABEL: Record<string, string> = {
  w_mm: "Length", d_mm: "Width", h_mm: "Height", r_mm: "Radius",
  r2_mm: "Top radius", sweep: "Sweep angle",
};

export function AcadBodyDialog({ chrome, open, kind, selCount, close, onMake }: {
  chrome: AcadChrome; open: boolean; kind: string; selCount: number;
  close: () => void; onMake: (kind: string, vals: Record<string, number>) => Promise<any>;
}) {
  const { ui } = chrome;
  const [vals, setVals] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEscape(open && !busy, close);
  useEffect(() => {
    if (!open) return;
    // ⚠ Defaults a drafter would not have to change for a first look: a 1 m cube, a 500 mm
    // radius. A dialog opening on zeroes makes an invisible solid and reads as a dead command.
    setVals({ w_mm: "1000", d_mm: "1000", h_mm: "1000", r_mm: "500",
              r2_mm: kind === "cone" || kind === "pyramid" ? "0" : "150", sweep: "360" });
    setErr(""); setBusy(false);
  }, [open, kind]);
  if (!open) return null;

  const fields = BODY_FIELDS[kind] || [];
  const fromProfile = ["extrude", "revolve", "loft", "sweep", "polysolid"].includes(kind);
  const row = { height: 24, background: ui.sunk, color: ui.fg, width: 120,
                border: `1px solid ${ui.edge}`, padding: "0 6px",
                font: `${ui.size}px ${ui.font}` } as React.CSSProperties;

  const go = async () => {
    const out: Record<string, number> = {};
    for (const f of fields) {
      const n = parseFloat(vals[f] ?? "");
      // ⚠ A top radius of 0 is a POINT and is the default for a cone and a pyramid, so zero is
      // valid for `r2_mm` and invalid for everything else. One rule for all of them would
      // either refuse a cone or accept a zero-height box.
      if (!Number.isFinite(n) || (n <= 0 && f !== "r2_mm")) {
        setErr(`${BODY_LABEL[f] || f} must be a positive number`); return;
      }
      out[f] = n;
    }
    setBusy(true); setErr("");
    try { await onMake(kind, out); close(); }
    catch (e: any) { setErr(e?.response?.data?.detail || String(e?.message || e)); }
    finally { setBusy(false); }
  };

  return (
    <div onClick={close}
      style={{ position: "fixed", inset: 0, zIndex: 963, background: "rgba(0,0,0,0.22)",
               font: `${ui.size}px ${ui.font}`, color: ui.fg }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="acad-body-dialog"
        style={{ position: "absolute", left: "50%", top: "18%", transform: "translateX(-50%)",
                 width: 380, background: ui.ribbon, border: `1px solid ${ui.menuLine}`,
                 boxShadow: "4px 5px 18px rgba(0,0,0,0.5)" }}>
        <div style={{ height: 26, display: "flex", alignItems: "center", padding: "0 10px",
                      fontWeight: 600, background: ui.title,
                      borderBottom: `1px solid ${ui.line}` }}>
          {kind.charAt(0).toUpperCase() + kind.slice(1)}
        </div>
        <div style={{ padding: 14 }}>
          {fromProfile ? (
            <div data-testid="acad-body-profile" style={{ marginBottom: 10, color: ui.soft }}>
              {selCount > 0
                ? `built from ${selCount} selected shape${selCount === 1 ? "" : "s"} — `
                  + "move the shape later and the body follows it"
                : "select a closed shape on the drawing first"}
            </div>) : null}
          {fields.map((f) => (
            <div key={f} style={{ display: "flex", alignItems: "center", gap: 10,
                                  marginBottom: 8 }}>
              <span style={{ width: 110 }}>{BODY_LABEL[f] || f}</span>
              <input data-testid={`acad-body-${f}`} style={row} value={vals[f] ?? ""}
                onChange={(e) => setVals((v) => ({ ...v, [f]: e.target.value }))} />
              <span style={{ color: ui.soft }}>{f === "sweep" ? "°" : "mm"}</span>
            </div>))}
          {err ? <div data-testid="acad-body-err"
                   style={{ color: "#E06C60", marginTop: 6 }}>⚠ {err}</div> : null}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
            <button data-testid="acad-body-cancel" onClick={close}
              style={{ padding: "4px 18px", cursor: "pointer", background: "transparent",
                       color: ui.fg, border: `1px solid ${ui.edge}`, borderRadius: 2,
                       font: `${ui.size}px ${ui.font}` }}>Cancel</button>
            <button data-testid="acad-body-ok" onClick={go}
              disabled={busy || (fromProfile && selCount === 0)}
              style={{ padding: "4px 18px", cursor: busy ? "wait" : "pointer",
                       background: ui.accent, color: "#fff", border: "none", borderRadius: 2,
                       opacity: fromProfile && selCount === 0 ? 0.5 : 1,
                       font: `${ui.size}px ${ui.font}` }}>
              {busy ? "Building…" : "OK"}</button>
          </div>
        </div>
      </div>
    </div>);
}


/** ── AutoCAD's block attributes: ATTDEF, BATTMAN, ATTSYNC and EATTEDIT in one window ────────
 *
 * ⚠⚠ **FOUR COMMANDS, ONE DIALOG, BECAUSE THEY ARE FOUR VIEWS OF ONE THING.** AutoCAD splits
 * them because ATTDEF predates the Block Attribute Manager by fifteen years; a drafter arriving
 * today opens BATTMAN to add a field, and ATTDEF is the same act with a different window. The
 * ribbon still carries all four names — pressing any of them opens this on the right tab, which
 * is what the names are for.
 *
 * ⚠ The DEFINITION tab edits the block for every drawing that uses it; the VALUES tab edits the
 * one instance selected. The window says which, because getting that backwards means editing
 * forty handles when you meant one.
 */
export function AcadAttrDialog({ chrome, open, tab, block, instance, close,
                                 load, save, sync, setValues }: {
  chrome: AcadChrome; open: boolean; tab: "defs" | "values";
  block: { id: number; name: string } | null;
  instance: { id: number; block: number; name: string } | null;
  close: () => void;
  load: (block?: number) => Promise<any>;
  save: (block: number, defs: any[]) => Promise<any>;
  sync: (block: number) => Promise<any>;
  setValues: (instance: number, values: Record<string, string>) => Promise<any>;
}) {
  const { ui } = chrome;
  const [defs, setDefs] = useState<any[]>([]);
  const [vals, setVals] = useState<Record<string, string>>({});
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  useEscape(open && !busy, close);
  useEffect(() => {
    if (!open) return;
    setMsg(""); setBusy(false);
    const id = block?.id || instance?.block;
    if (!id) { setDefs([]); return; }
    load(id).then((r) => {
      setDefs(r?.defs || []);
      // ⚠ Values come from the SCHEDULE read, not from a second endpoint — one source for the
      // resolved value (a typed one, or the definition's default) so the dialog cannot show a
      // different number than the schedule prints.
      if (instance) {
        load().then((all) => {
          const row = (all?.schedule || []).find((s: any) => s.block_id === instance.block);
          setVals(row?.attrs || {});
        }).catch(() => setVals({}));
      }
    }).catch(() => setDefs([]));
  }, [open, block?.id, instance?.id]);
  if (!open) return null;

  const cell = { height: 22, background: ui.sunk, color: ui.fg,
                 border: `1px solid ${ui.edge}`, padding: "0 5px",
                 font: `${ui.size}px ${ui.font}` } as React.CSSProperties;
  const target = block?.name || instance?.name || "";

  return (
    <div onClick={close}
      style={{ position: "fixed", inset: 0, zIndex: 964, background: "rgba(0,0,0,0.22)",
               font: `${ui.size}px ${ui.font}`, color: ui.fg }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="acad-attr-dialog"
        style={{ position: "absolute", left: "50%", top: "13%", transform: "translateX(-50%)",
                 // ⚠ 620, not 540: at 540 the "Show" column ran off the right edge and the
                 // header read "S". Measured on a screenshot, which is the only way that kind
                 // of thing is ever noticed.
                 width: 620, background: ui.ribbon, border: `1px solid ${ui.menuLine}`,
                 boxShadow: "4px 5px 18px rgba(0,0,0,0.5)" }}>
        <div style={{ height: 26, display: "flex", alignItems: "center", padding: "0 10px",
                      fontWeight: 600, background: ui.title,
                      borderBottom: `1px solid ${ui.line}` }}>
          {tab === "defs" ? "Block Attribute Manager" : "Edit Attributes"}
          <span style={{ marginLeft: 8, color: ui.soft, fontWeight: 400 }}>
            — {target || "no block"}</span>
        </div>
        <div style={{ padding: 14, maxHeight: 420, overflow: "auto" }}>
          {!target ? (
            <div data-testid="acad-attr-none" style={{ color: ui.soft }}>
              {tab === "defs"
                ? "Select a placed block first — attributes belong to its definition."
                : "Select one placed block to edit its values."}
            </div>
          ) : tab === "defs" ? (
            <>
              <div style={{ color: ui.soft, marginBottom: 8 }}>
                These fields belong to <b>{target}</b> and apply to every drawing that uses it.
              </div>
              <div style={{ display: "grid",
                            gridTemplateColumns: "1.1fr 1.4fr 1.2fr 44px 26px",
                            gap: 6, alignItems: "center" }}>
                <b>Tag</b><b>Prompt</b><b>Default</b><b>Show</b><span />
                {defs.map((d, i) => (
                  <React.Fragment key={i}>
                    <input data-testid={`acad-attr-tag-${i}`} style={cell} value={d.tag || ""}
                      onChange={(e) => setDefs(defs.map((x, j) =>
                        j === i ? { ...x, tag: e.target.value } : x))} />
                    <input style={cell} value={d.prompt || ""}
                      onChange={(e) => setDefs(defs.map((x, j) =>
                        j === i ? { ...x, prompt: e.target.value } : x))} />
                    <input style={cell} value={d.default || ""}
                      onChange={(e) => setDefs(defs.map((x, j) =>
                        j === i ? { ...x, default: e.target.value } : x))} />
                    <input type="checkbox" checked={d.visible !== false}
                      onChange={(e) => setDefs(defs.map((x, j) =>
                        j === i ? { ...x, visible: e.target.checked } : x))} />
                    <span style={{ cursor: "pointer", color: ui.soft, textAlign: "center" }}
                      title="Remove this attribute"
                      onClick={() => setDefs(defs.filter((_, j) => j !== i))}>×</span>
                  </React.Fragment>))}
              </div>
              <button data-testid="acad-attr-add"
                onClick={() => setDefs([...defs, { tag: "", prompt: "", default: "",
                                                   visible: true }])}
                style={{ marginTop: 10, padding: "3px 12px", cursor: "pointer",
                         background: "transparent", color: ui.fg,
                         border: `1px solid ${ui.edge}`, borderRadius: 2,
                         font: `${ui.size}px ${ui.font}` }}>+ Attribute</button>
            </>
          ) : (
            <>
              <div style={{ color: ui.soft, marginBottom: 8 }}>
                These values belong to <b>this one</b> placed block.
              </div>
              {defs.length === 0
                ? <div data-testid="acad-attr-nodefs" style={{ color: ui.soft }}>
                    {target} has no attributes — define them with Block Attribute Manager first.
                  </div>
                : defs.map((d) => (
                  <div key={d.tag} style={{ display: "flex", alignItems: "center", gap: 10,
                                            marginBottom: 7 }}>
                    <span style={{ width: 150 }} title={d.prompt || ""}>{d.tag}</span>
                    <input data-testid={`acad-attr-val-${d.tag}`}
                      style={{ ...cell, flex: 1 }} value={vals[d.tag] ?? ""}
                      onChange={(e) => setVals({ ...vals, [d.tag]: e.target.value })} />
                  </div>))}
            </>
          )}
          {msg ? <div data-testid="acad-attr-msg"
                   style={{ marginTop: 10, color: ui.soft }}>{msg}</div> : null}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
            {tab === "defs" && block ? (
              <button data-testid="acad-attr-sync" disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    const r = await sync(block.id);
                    setMsg(`${r.instances} placed block(s) updated — `
                           + `${r.added} field(s) added, ${r.removed} removed. `
                           + "Values you had typed were kept.");
                  } finally { setBusy(false); }
                }}
                style={{ padding: "4px 14px", cursor: "pointer", background: "transparent",
                         color: ui.fg, border: `1px solid ${ui.edge}`, borderRadius: 2,
                         marginRight: "auto", font: `${ui.size}px ${ui.font}` }}>
                Sync placed blocks</button>) : null}
            <button data-testid="acad-attr-cancel" onClick={close}
              style={{ padding: "4px 18px", cursor: "pointer", background: "transparent",
                       color: ui.fg, border: `1px solid ${ui.edge}`, borderRadius: 2,
                       font: `${ui.size}px ${ui.font}` }}>Close</button>
            {target ? (
              <button data-testid="acad-attr-ok" disabled={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    if (tab === "defs" && block) {
                      const r = await save(block.id, defs);
                      setDefs(r?.defs || []);
                      setMsg(r?.dropped
                        ? `${r.dropped} row(s) had no usable tag and were dropped.`
                        : "Saved.");
                    } else if (instance) {
                      await setValues(instance.id, vals);
                      close();
                    }
                  } finally { setBusy(false); }
                }}
                style={{ padding: "4px 18px", cursor: busy ? "wait" : "pointer",
                         background: ui.accent, color: "#fff", border: "none", borderRadius: 2,
                         font: `${ui.size}px ${ui.font}` }}>
                {busy ? "Saving…" : "OK"}</button>) : null}
          </div>
        </div>
      </div>
    </div>);
}


/** ── AutoCAD's Parameters Manager, and the constraints it drives ────────────────────────────
 *
 * ⚠⚠ **ONE GRID FOR BOTH, BECAUSE AutoCAD'S OWN PARAMETERS MANAGER IS ONE GRID FOR BOTH.** A
 * dimensional constraint IS a parameter with geometry attached: it has a name, an expression and
 * a value, and `gap = d1 / 3` is the whole reason the window exists. Splitting them would mean
 * typing a constraint's name into a table that cannot see it.
 *
 * ⚠ Editing an expression SOLVES on the spot and the drawing reloads. A window that stored a
 * number and waited for a Solve button would make a constraint look exactly like a dimension —
 * and a dimension that moves nothing is what this app already had.
 */
export function AcadParamsDialog({ chrome, open, close, load, post, del }: {
  chrome: AcadChrome; open: boolean; close: () => void;
  load: () => Promise<any>;
  post: (body: any) => Promise<any>;
  del: (q: string) => Promise<any>;
}) {
  const { ui } = chrome;
  const [data, setData] = useState<any>({ params: [], constraints: [], errors: {} });
  const [rows, setRows] = useState<any[]>([]);
  const [busy, setBusy] = useState(false);
  useEscape(open && !busy, close);
  const refresh = React.useCallback(() => {
    load().then((r) => { setData(r); setRows(r.params || []); }).catch(() => undefined);
  }, [load]);
  useEffect(() => { if (open) refresh(); }, [open, refresh]);
  if (!open) return null;

  const cell = { height: 22, background: ui.sunk, color: ui.fg, width: "100%",
                 border: `1px solid ${ui.edge}`, padding: "0 5px",
                 font: `${ui.size}px ${ui.font}` } as React.CSSProperties;
  const errs: Record<string, string> = data.errors || {};
  const num = (v: any) => (typeof v === "number" ? String(Math.round(v * 1000) / 1000) : "—");
  const run = async (fn: () => Promise<any>) => {
    setBusy(true);
    try { const r = await fn(); setData(r); setRows(r.params || []); }
    finally { setBusy(false); }
  };

  return (
    <div onClick={close}
      style={{ position: "fixed", inset: 0, zIndex: 965, background: "rgba(0,0,0,0.22)",
               font: `${ui.size}px ${ui.font}`, color: ui.fg }}>
      <div onClick={(e) => e.stopPropagation()} data-testid="acad-params-dialog"
        style={{ position: "absolute", left: "50%", top: "10%", transform: "translateX(-50%)",
                 width: 660, background: ui.ribbon, border: `1px solid ${ui.menuLine}`,
                 boxShadow: "4px 5px 18px rgba(0,0,0,0.5)" }}>
        <div style={{ height: 26, display: "flex", alignItems: "center", padding: "0 10px",
                      fontWeight: 600, background: ui.title,
                      borderBottom: `1px solid ${ui.line}` }}>Parameters Manager</div>
        <div style={{ padding: 14, maxHeight: 460, overflow: "auto" }}>
          <div style={{ color: ui.soft, marginBottom: 6 }}>
            Dimensional constraints — change a value and the geometry moves.
          </div>
          {(data.constraints || []).length === 0 ? (
            <div data-testid="acad-params-nocon" style={{ color: ui.soft, marginBottom: 12 }}>
              None yet. Select a line and press Linear, Aligned, Angular or Radial on the
              Parametric tab — the new constraint holds the size it already has.
            </div>
          ) : (
            <div style={{ display: "grid",
                          gridTemplateColumns: "70px 84px 1fr 78px 78px 26px",
                          gap: 6, alignItems: "center", marginBottom: 14 }}>
              <b>Name</b><b>Type</b><b>Expression</b><b>Value</b><b>Measured</b><span />
              {(data.constraints || []).map((c: any) => (
                <React.Fragment key={c.id}>
                  <span data-testid={`acad-con-name-${c.id}`}>{c.name || `#${c.id}`}</span>
                  <span style={{ color: ui.soft }}>{c.kind}</span>
                  <input data-testid={`acad-con-expr-${c.id}`} style={cell}
                    defaultValue={c.expr}
                    onBlur={(e) => {
                      if (e.target.value === c.expr) return;
                      run(() => post({ id: c.id, expr: e.target.value }));
                    }}
                    onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }} />
                  <span data-testid={`acad-con-value-${c.id}`}>{num(c.value)}</span>
                  {/* ⚠ MEASURED is what the geometry actually reads right now. When it differs
                      from Value the constraint could not be applied — an expression that does
                      not resolve, or geometry it cannot move — and this column is the only place
                      that shows it. Silent disagreement is how a constraint stops driving and
                      nobody notices. */}
                  <span data-testid={`acad-con-now-${c.id}`}
                    style={{ color: Math.abs((c.now ?? 0) - (c.value ?? c.now ?? 0)) > 0.05
                      ? "#E06C60" : ui.soft }}>{num(c.now)}</span>
                  <span style={{ cursor: "pointer", color: ui.soft, textAlign: "center" }}
                    title="Delete this constraint"
                    onClick={() => run(() => del(`id=${c.id}`))}>×</span>
                </React.Fragment>))}
            </div>)}

          <div style={{ color: ui.soft, marginBottom: 6 }}>
            Parameters — named numbers a constraint can be written in terms of.
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "110px 1fr 78px 26px",
                        gap: 6, alignItems: "center" }}>
            <b>Name</b><b>Expression</b><b>Value</b><span />
            {rows.map((r: any, i: number) => (
              <React.Fragment key={i}>
                <input data-testid={`acad-param-name-${i}`} style={cell} value={r.name || ""}
                  onChange={(e) => setRows(rows.map((x, j) =>
                    j === i ? { ...x, name: e.target.value } : x))} />
                <input data-testid={`acad-param-expr-${i}`} style={cell} value={r.expr || ""}
                  onChange={(e) => setRows(rows.map((x, j) =>
                    j === i ? { ...x, expr: e.target.value } : x))} />
                <span data-testid={`acad-param-value-${i}`}
                  style={{ color: errs[(r.name || "").toUpperCase()] ? "#E06C60" : ui.fg }}>
                  {errs[(r.name || "").toUpperCase()] ? "⚠" : num(r.value)}</span>
                <span style={{ cursor: "pointer", color: ui.soft, textAlign: "center" }}
                  onClick={() => setRows(rows.filter((_, j) => j !== i))}>×</span>
              </React.Fragment>))}
          </div>
          {Object.keys(errs).length ? (
            <div data-testid="acad-params-err" style={{ marginTop: 8, color: "#E06C60" }}>
              {Object.entries(errs).map(([k, v]) => <div key={k}>⚠ {k}: {v}</div>)}
            </div>) : null}
          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <button data-testid="acad-param-add"
              onClick={() => setRows([...rows, { name: "", expr: "0" }])}
              style={{ padding: "3px 12px", cursor: "pointer", background: "transparent",
                       color: ui.fg, border: `1px solid ${ui.edge}`, borderRadius: 2,
                       font: `${ui.size}px ${ui.font}` }}>+ Parameter</button>
            <button data-testid="acad-param-solve" disabled={busy}
              onClick={() => run(() => post({ op: "solve" }))}
              style={{ padding: "3px 12px", cursor: "pointer", background: "transparent",
                       color: ui.fg, border: `1px solid ${ui.edge}`, borderRadius: 2,
                       font: `${ui.size}px ${ui.font}` }}>Re-apply all</button>
            <div style={{ flex: 1 }} />
            <button data-testid="acad-params-close" onClick={close}
              style={{ padding: "4px 18px", cursor: "pointer", background: "transparent",
                       color: ui.fg, border: `1px solid ${ui.edge}`, borderRadius: 2,
                       font: `${ui.size}px ${ui.font}` }}>Close</button>
            <button data-testid="acad-params-ok" disabled={busy}
              onClick={() => run(() => post({ params: rows }))}
              style={{ padding: "4px 18px", cursor: busy ? "wait" : "pointer",
                       background: ui.accent, color: "#fff", border: "none", borderRadius: 2,
                       font: `${ui.size}px ${ui.font}` }}>{busy ? "Solving…" : "Apply"}</button>
          </div>
        </div>
      </div>
    </div>);
}
